import { Platform } from 'react-native';
import { paraGuardar } from '../constants/vehiculoDeFlota';
import { db } from './firebaseConfig';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDocs,
  query,
  where,
  onSnapshot,
  serverTimestamp,
  setDoc,
  getDoc,
  orderBy,
  limit,
  deleteDoc,
  runTransaction,
  Timestamp,
  arrayUnion,
} from 'firebase/firestore';
// SDK NATIVO de Firestore — necesario en RN porque el SDK web va anónimo
// cuando el user se autenticó vía REST (Play Integrity bloqueado por el
// operador). El nativo SÍ comparte sesión con `getNativeAuth()` después
// del `signInWithCustomToken` que dispara authBridge.
import nativeFirestore from '@react-native-firebase/firestore';
import { zonaDe, zonasVecinas, haversineKm } from './geoUtils';

// ═══════════════════════════════════════════════════════════════════════════
// ESCRITURAS QUE ELIGEN EL SDK CORRECTO SEGÚN LA PLATAFORMA
// ═══════════════════════════════════════════════════════════════════════════
//
// EL FALLO QUE ESTO CIERRA
// En Android el SDK WEB de Firestore va SIN SESIÓN (el login real vive en
// `@react-native-firebase` vía el custom token de authBridge). Cualquier
// escritura hecha con `setDoc`/`addDoc`/`updateDoc`/`deleteDoc` del SDK web
// llega al servidor como anónima y las reglas la DENIEGAN.
//
// Varios métodos de este archivo ya tenían su rama nativa — y el comentario de
// `listenToOnlineDrivers` documenta ese mismo arreglo. Pero 18 métodos se
// quedaron sin ella, y el patrón habitual es un `catch` que solo hace
// `console.warn`: el usuario ve que la UI cambia, cree que se guardó, y no se
// guardó nada. El caso más caro era `setDriverOnline`: el chofer se ponía
// "En línea", se quedaba esperando viajes, y ningún pasajero lo veía nunca
// porque `/drivers/{uid}` jamás se escribió.
//
// Con estos ayudantes la decisión de plataforma se toma en UN solo sitio.
// Úsalos SIEMPRE para escribir; no vuelvas a llamar a setDoc/addDoc/updateDoc
// /deleteDoc directamente desde un método de este servicio.

/** Marca de tiempo del servidor, en el dialecto del SDK que toca. */
function selloServidor(): any {
  return Platform.OS === 'web'
    ? serverTimestamp()
    : nativeFirestore.FieldValue.serverTimestamp();
}

/**
 * Cuánto dura el permiso que deja al pasajero ver la posición viva de su
 * chofer. Es EL MISMO número que `VENTANA_PERMISO_MS` en
 * functions/src/rides.ts, a propósito: esta app es el respaldo de ese
 * disparador, y un respaldo con otra ventana deja de respaldar lo mismo.
 * El porqué del 6 está escrito entero en `setDriverActivePassenger`.
 */
const VENTANA_PERMISO_UBICACION_MS = 6 * 60 * 60 * 1000;

/**
 * Un Timestamp de dentro de `ms`, en el dialecto del SDK que toca.
 *
 * ⚠️ ESTO USA EL RELOJ DEL TELÉFONO y la regla lo compara contra el del
 * servidor (`request.time`). No hay forma de escribir «serverTimestamp + 6 h»
 * desde el cliente: `FieldValue.serverTimestamp()` no admite desplazamiento.
 * O sea que un teléfono con la hora torcida escribe un permiso torcido: si va
 * atrasado más de 6 h, nace vencido (el pasajero se queda sin mapa hasta que
 * llegue el permiso del servidor, que es el que manda); si va adelantado, dura
 * de más. Se asume a sabiendas porque el que concede el permiso de verdad es
 * el servidor —con su reloj— y esto solo cubre el hueco de unos segundos hasta
 * que llega su escritura. Si algún día hiciera falta cerrar también ese hueco,
 * la salida es que la app deje de escribir esta llave, no que escriba una
 * caducidad peor.
 */
function selloDentroDe(ms: number): any {
  const cuando = Date.now() + ms;
  return Platform.OS === 'web'
    ? Timestamp.fromMillis(cuando)
    : nativeFirestore.Timestamp.fromMillis(cuando);
}

/** Referencia nativa a partir de segmentos: ['users','abc','students'] */
function refNativa(segmentos: string[]): any {
  let ref: any = nativeFirestore();
  for (let i = 0; i < segmentos.length; i += 2) {
    ref = ref.collection(segmentos[i]);
    if (segmentos[i + 1] !== undefined) ref = ref.doc(segmentos[i + 1]);
  }
  return ref;
}

/**
 * Quita del objeto las claves cuyo valor es `undefined`.
 *
 * POR QUE EXISTE: Firestore RECHAZA `undefined`. No lo ignora: lanza
 * "Unsupported field value: undefined" ANTES de salir del telefono, o sea que
 * ni siquiera llega a la red. Y como el error sube hasta el catch de la
 * pantalla, el usuario veia "Revisa tu conexion" cuando su conexion estaba
 * perfecta.
 *
 * Lo encontro el fundador probando en vivo (14-oct-2026): en "Mis direcciones"
 * el campo "Referencia adicional (OPCIONAL)" era en la practica obligatorio,
 * porque `reference: reference.trim() || undefined` mandaba undefined cuando se
 * dejaba en blanco. Lo mismo pasaba al no fijar el punto en el mapa
 * (`lat: pickedLat ?? undefined`). Medido con el SDK real: con referencia
 * escrita pasa; en blanco, rechazado.
 *
 * Se arregla AQUI y no en la pantalla a proposito: el patron `campo ?? undefined`
 * para "este dato es opcional" esta repartido por toda la app, asi que taparlo
 * en una pantalla dejaria el mismo fallo esperando en las demas.
 *
 * Solo baja por objetos PLANOS. No toca arrays, fechas, Timestamp, GeoPoint ni
 * los centinelas de Firestore (serverTimestamp, deleteField, arrayUnion): esos
 * son instancias de clase y clonarlos los rompe. `deleteField()` sigue
 * funcionando para borrar un campo de verdad, que es distinto de "no lo mandes".
 */
function sinUndefined<T>(datos: T): T {
  if (datos === null || typeof datos !== 'object') return datos;
  if (Object.getPrototypeOf(datos) !== Object.prototype) return datos;
  const salida: any = {};
  for (const [clave, valor] of Object.entries(datos as any)) {
    if (valor === undefined) continue;
    salida[clave] = sinUndefined(valor);
  }
  return salida as T;
}

/**
 * Crea o mezcla un documento. `segmentos` alterna colección/documento:
 *   guardarDoc(['drivers', uid], datos, true)
 *   guardarDoc(['users', uid, 'students', sid], datos)
 */
async function guardarDoc(segmentos: string[], datos: any, mezclar = true): Promise<void> {
  const limpios = sinUndefined(datos);
  if (Platform.OS !== 'web') {
    await refNativa(segmentos).set(limpios, { merge: mezclar });
    return;
  }
  await setDoc(doc(db, segmentos[0], ...segmentos.slice(1)), limpios, { merge: mezclar });
}

/** Añade un documento con id automático y devuelve su id. */
async function agregarDoc(segmentos: string[], datos: any): Promise<string> {
  const limpios = sinUndefined(datos);
  if (Platform.OS !== 'web') {
    const r = await refNativa(segmentos).add(limpios);
    return r.id;
  }
  const r = await addDoc(collection(db, segmentos[0], ...segmentos.slice(1)), limpios);
  return r.id;
}

/** Actualiza campos de un documento que YA existe. */
async function actualizarDoc(segmentos: string[], datos: any): Promise<void> {
  const datosLimpios = sinUndefined(datos);
  if (Platform.OS !== 'web') {
    await refNativa(segmentos).update(datosLimpios);
    return;
  }
  await updateDoc(doc(db, segmentos[0], ...segmentos.slice(1)), datosLimpios);
}

/**
 * Ids de las direcciones guardadas del usuario, con su bandera `isDefault`.
 * Se lee con el SDK que toca; el web en móvil devolvía vacío por falta de
 * sesión, así que "poner como predeterminada" no cambiaba nada.
 */
async function listarIdsDeDirecciones(uid: string): Promise<Array<{ id: string; isDefault: boolean }>> {
  if (Platform.OS !== 'web') {
    const snap = await nativeFirestore()
      .collection('users')
      .doc(uid)
      .collection('addresses')
      .get();
    return snap.docs.map((d: any) => ({ id: d.id, isDefault: !!d.data()?.isDefault }));
  }
  const snap = await getDocs(collection(db, 'users', uid, 'addresses'));
  return snap.docs.map((d) => ({ id: d.id, isDefault: !!d.data()?.isDefault }));
}

/** Borra un documento. */
async function borrarDoc(segmentos: string[]): Promise<void> {
  if (Platform.OS !== 'web') {
    await refNativa(segmentos).delete();
    return;
  }
  await deleteDoc(doc(db, segmentos[0], ...segmentos.slice(1)));
}

// ─────────────────────────────────────────────────────────────────────────
// FRESCURA DE RIDES — Anti "viaje fantasma"
// ─────────────────────────────────────────────────────────────────────────
// Un ride en Firestore que quedó ACCEPTED/ARRIVED/IN_PROGRESS y nunca se
// completó ni canceló (típico de pruebas interrumpidas o crashes) NO debe
// resucitar al reconectarse el chofer/pasajero. Un viaje real en R.D. dura
// máximo ~1.5h; cualquier ride cuya última actividad sea de hace más de
// STALE_RIDE_MS se considera ABANDONADO. Se filtra de las queries de resume
// y se auto-cancela para limpiar la basura del backend de una vez.
const STALE_RIDE_MS = 4 * 60 * 60 * 1000; // 4 horas — generoso pero seguro

/** Millis de la última actividad conocida del ride (0 si desconocida). */
function rideLastActivityMs(ride: any): number {
  const ts = (v: any) => v?.toMillis?.() ?? v?.toDate?.()?.getTime?.() ?? 0;
  return Math.max(
    ts(ride?.startedAt),
    ts(ride?.arrivedAt),
    ts(ride?.acceptedAt),
    ts(ride?.updatedAt),
    ts(ride?.createdAt),
  );
}

/**
 * ¿El ride sigue "vivo"? true si su última actividad es reciente.
 * Si NO tiene ningún timestamp legible, lo tratamos como FRESCO SOLO cuando
 * viene de un snapshot en vivo (nuevo doc sin serverTimestamp resuelto aún),
 * pero como STALE en las consultas de resume/bootstrap para no revivir
 * basura sin fecha. El flag `assumeFreshIfNoTs` controla ese matiz.
 *
 * `ventanaMs` existe para UN solo llamador: el candado de `requestRide` con
 * los viajes IN_PROGRESS (H58, auditoría 16-sep-2026). Todos los demás usan
 * las 4 h de siempre, y deben seguir así: a partir de esa ventana el cliente
 * AUTO-EXPIRA el viaje, y una ventana más corta ahí cancelaría viajes largos
 * de verdad (Santo Domingo–Punta Cana pasa de las 2 h).
 */
function isRideFresh(ride: any, assumeFreshIfNoTs = false, ventanaMs = STALE_RIDE_MS): boolean {
  const last = rideLastActivityMs(ride);
  if (last === 0) return assumeFreshIfNoTs;
  return Date.now() - last < ventanaMs;
}

/**
 * Cuánto tiempo bloquea `requestRide` un viaje IN_PROGRESS del pasajero.
 * Ver el comentario de H58 dentro de `requestRide`.
 */
const VENTANA_CANDADO_EN_CURSO_MS = 90 * 60 * 1000;

// Ids que ya intentamos auto-cancelar en esta sesión — evita writes duplicados
// mientras el cancel está en vuelo y el snapshot vuelve a disparar.
const _autoExpiredRideIds = new Set<string>();

/** Fire-and-forget: marca un ride viejo como CANCELLED por expiración. */
function autoExpireStaleRide(rideId: string) {
  if (!rideId || _autoExpiredRideIds.has(rideId)) return;
  _autoExpiredRideIds.add(rideId);
  const payload = {
    status: 'CANCELLED',
    cancelReason: 'auto_expired_stale',
    cancelledAt: (Platform.OS !== 'web'
      ? nativeFirestore.FieldValue.serverTimestamp()
      : serverTimestamp()),
  };
  const p = Platform.OS !== 'web'
    ? nativeFirestore().collection('rides').doc(rideId).update(payload)
    : updateDoc(doc(db, 'rides', rideId), payload);
  Promise.resolve(p).catch((e: any) =>
    console.warn('[autoExpireStaleRide] no se pudo expirar', rideId, e?.message)
  );
}

// ===========================================================================
// ESPEJO GRUESO DE POSICIÓN PARA /drivers  (fix P0 — causa raíz #3)
// ---------------------------------------------------------------------------
// /driverLocations = posición EXACTA, en cada latido, con la regla anti-acoso
//                    dura (solo el chofer, su pasajero del viaje vivo, los
//                    tutores de la ruta escolar y su director).
// /drivers         = directorio PÚBLICO de quién está trabajando. Lo escucha
//                    cualquiera con sesión, así que aquí solo puede vivir una
//                    CELDA de ~110 m: suficiente para pintar los carritos del
//                    mapa y para que el dispatch ordene candidatos por
//                    cercanía (radios de kilómetros), e inservible para
//                    seguirle el paso a nadie por la calle.
//
// El racionado (un espejo cada 20 s por chofer, y solo si cambió de celda) es
// lo que apaga la tormenta de snapshots: un chofer parado esperando viaje —la
// mayoría del tiempo de la mayoría de la flota— deja de generar tráfico.
// ===========================================================================

/** 3 decimales ≈ 110 m de lado. Es el grano del mapa público. */
const CELDA_DECIMALES = 3;
/** Techo de escrituras al espejo por chofer. No bajar sin medir. */
const ESPEJO_MIN_MS = 20_000;

const _ultimoEspejo = new Map<string, { celda: string; ms: number }>();

/** Redondea a la celda pública. */
function aCelda(v: number): number {
  const factor = Math.pow(10, CELDA_DECIMALES);
  return Math.round(v * factor) / factor;
}

/**
 * Decide si toca refrescar el espejo público de este chofer.
 * Devuelve null cuando NO hay que escribir (misma celda, o aún dentro del
 * racionado).
 *
 * La marca se pone AL DECIDIR, no al confirmar la escritura: sin red, el
 * `set` de Firestore se queda encolado sin resolver ni rechazar, y esperar a
 * que resolviera dejaría el racionado desarmado justo cuando peor está la
 * conexión. Si la escritura FALLA de verdad, el caller llama `revertir()` y
 * el próximo latido lo intenta otra vez.
 */
function espejoGruesoParaDrivers(
  driverId: string,
  lat: number,
  lng: number
): { datos: Record<string, any>; revertir: () => void } | null {
  if (!driverId) return null;
  if (typeof lat !== 'number' || typeof lng !== 'number') return null;
  if (!isFinite(lat) || !isFinite(lng)) return null;

  const celdaLat = aCelda(lat);
  const celdaLng = aCelda(lng);
  const celda = `${celdaLat},${celdaLng}`;
  const previo = _ultimoEspejo.get(driverId);

  if (previo) {
    if (previo.celda === celda) return null;
    if (Date.now() - previo.ms < ESPEJO_MIN_MS) return null;
  }
  _ultimoEspejo.set(driverId, { celda, ms: Date.now() });
  return {
    datos: { lat: celdaLat, lng: celdaLng, updatedAt: selloServidor() },
    revertir: () => {
      if (previo) _ultimoEspejo.set(driverId, previo);
      else _ultimoEspejo.delete(driverId);
    },
  };
}

/**
 * Olvida la última celda escrita. Se llama al cambiar la presencia para que
 * el primer latido del próximo turno vuelva a publicar la posición aunque la
 * app nunca se haya cerrado (si no, un chofer que se apaga, se mueve dos
 * cuadras y se vuelve a encender aparecería en la esquina de antes).
 */
function olvidarEspejo(driverId: string) {
  _ultimoEspejo.delete(driverId);
}

export const firestoreService = {
  // --- DRIVER PRESENCE (online/offline + vehicleType) ---
  async setDriverOnline(driverId: string, payload: {
    isOnline: boolean;
    vehicleType?: 'CAR' | 'MOTORCYCLE' | 'MINIBUS' | 'BICYCLE';
    lat?: number;
    lng?: number;
    userName?: string;
    rating?: string | number;
  }) {
    // 1) Escribir presencia en /drivers/{uid} — esto es lo que ven los pasajeros.
    // L2 FIX — Antes escribíamos `vehicleType: payload.vehicleType ?? null`
    // que sobrescribía con null en cada llamada que no incluía el tipo (ej.
    // el resume al reabrir la app). Ahora solo incluimos los campos que
    // vienen definidos; merge:true preserva los demás.
    //
    // FIX CRÍTICO — Esto escribía con el SDK WEB, que en Android va sin sesión:
    // la regla denegaba y el chofer se quedaba "En línea" en su pantalla sin
    // que ningún pasajero lo viera JAMÁS. El `catch` de map.tsx solo hacía
    // console.warn, así que no había ni un aviso. Ahora va por `guardarDoc`,
    // que elige el SDK nativo en móvil.
    const doc_payload: Record<string, any> = {
      isOnline: payload.isOnline,
      updatedAt: selloServidor(),
    };
    if (payload.vehicleType !== undefined) doc_payload.vehicleType = payload.vehicleType;
    // La posición que se publica aquí es la MISMA celda gruesa de ~110 m que
    // usa el espejo (ver espejoGruesoParaDrivers): /drivers es lectura pública
    // y no puede llevar la posición exacta de nadie ni al ponerse en línea.
    if (typeof payload.lat === 'number' && isFinite(payload.lat)) doc_payload.lat = aCelda(payload.lat);
    if (typeof payload.lng === 'number' && isFinite(payload.lng)) doc_payload.lng = aCelda(payload.lng);
    if (payload.userName !== undefined) doc_payload.userName = payload.userName;
    if (payload.rating !== undefined) doc_payload.rating = payload.rating;

    // ── FICHA PÚBLICA DEL CARRO (bloque D) ────────────────────────────────
    // El pasajero no tenía cómo saber en qué vehículo se va a montar: la ficha
    // de /drivers solo llevaba tipo, posición y nombre. Los cuatro datos del
    // carro sí existen —los escribe el KYC en /users/{uid}— pero nadie los
    // espejeaba aquí, así que la burbuja del mapa no tenía nada que pintar.
    //
    // ⚠️ /drivers ES LECTURA PARA CUALQUIER USUARIO CON SESIÓN. Por eso esta
    // copia es una LISTA BLANCA campo por campo, nunca un `...perfil`: aquí
    // solo puede viajar lo que identifica al CARRO en la calle —marca, modelo
    // y color—. Teléfono, correo, cédula, documentos, cuentas de banco y
    // ubicación exacta NO entran.
    //
    // ⚠️ LA PLACA SE SACÓ DE AQUÍ (segunda vuelta). Suelta, en el parqueo, una
    // placa no es secreto; pero esta colección se lee ENTERA con una consulta
    // (`where isOnline == true`), así que espejearla convertía /drivers en un
    // padrón de matrículas con la posición de cada carro al lado, servido a
    // cualquiera con una cuenta. La placa le llega al pasajero por las dos vías
    // acotadas de siempre: el documento de SU viaje (la escribe acceptRideOffer)
    // y `getDriverPublicInfo`, que solo responde por un chofer a la vez.
    //
    // ⚠️ Y `driverPhoto` / `isDriverVerified` NO se escriben aquí A PROPÓSITO:
    // la regla de /drivers (firestore.rules:424-425) DENIEGA toda actualización
    // que toque esos dos campos —son el sello de verificación, los pone kyc.ts
    // con el Admin SDK—. Meterlos en este payload no añadiría la foto: haría
    // fallar la escritura entera y dejaría al chofer invisible en el mapa.
    //
    // Solo al ENCENDER la presencia: es una lectura facturada y el apagado no
    // necesita el dato.
    if (payload.isOnline === true) {
      try {
        const perfil: any = Platform.OS !== 'web'
          ? await (async () => {
              const s: any = await nativeFirestore().collection('users').doc(driverId).get();
              return s?.exists?.() ? s.data() : null;
            })()
          : await (async () => {
              const s = await getDoc(doc(db, 'users', driverId));
              return s.exists() ? s.data() : null;
            })();
        // Un dato que no existe se OMITE (merge:true conserva lo que ya había);
        // nunca se rellena con un valor plausible ni se pisa con null.
        const texto = (v: any, max: number): string | undefined => {
          if (typeof v !== 'string') return undefined;
          const t = v.trim().slice(0, max);
          return t.length > 0 ? t : undefined;
        };
        const marca = texto(perfil?.vehicleBrand, 40);
        const modelo = texto(perfil?.vehicleModel, 40);
        const color = texto(perfil?.vehicleColor, 30);
        if (marca !== undefined) doc_payload.vehicleBrand = marca;
        if (modelo !== undefined) doc_payload.vehicleModel = modelo;
        if (color !== undefined) doc_payload.vehicleColor = color;
        // `licensePlate` NO se espejea aquí a propósito — ver el aviso de
        // arriba. Si algún día vuelve a hacer falta en el mapa, tendrá que ser
        // con una regla que impida leer la colección entera de una vez.
      } catch (e: any) {
        // Que no se pueda leer el perfil no puede impedir que el chofer se
        // ponga en línea: se publica la presencia sin los datos del carro.
        console.warn('[setDriverOnline] no se pudo espejear el vehículo:', e?.message);
      }
    }

    // Cambiar de turno reinicia el racionado: el primer latido después de
    // encender la presencia tiene que publicar la posición sí o sí.
    olvidarEspejo(driverId);
    await guardarDoc(['drivers', driverId], doc_payload, true);

    // 2) Mirror del vehicleType en /users/{uid} — lo necesitan las Cloud
    //    Functions (getMonthlyFee, trackDriverTrip) para calcular cuota y bonos.
    //    Sin esto, getMonthlyFee cae al default por rol en vez de ser exacto.
    if (payload.vehicleType) {
      await guardarDoc(['users', driverId], {
        vehicleType: payload.vehicleType,
        updatedAt: selloServidor(),
      }, true);
    }
  },

  /**
   * Persiste el tipo de vehículo del chofer al iniciar el onboarding KYC.
   * Lo llama kyc-welcome al elegir CAR / MOTORCYCLE / MINIBUS / BICYCLE.
   * Así el `vehicleType` queda en /users/{uid} desde el primer minuto y las
   * Cloud Functions calculan cuota correcta aún antes de que se ponga online.
   */
  async setDriverVehicleType(driverId: string, vehicleType: 'CAR' | 'MOTORCYCLE' | 'MINIBUS' | 'BICYCLE') {
    const userRef = doc(db, 'users', driverId);
    await setDoc(userRef, {
      vehicleType,
      updatedAt: serverTimestamp(),
    }, { merge: true });
  },

  listenToOnlineDrivers(callback: (drivers: any[]) => void, vehicleType?: 'CAR' | 'MOTORCYCLE' | 'MINIBUS') {
    // FIX CRÍTICO DISCOVERY — Antes esto usaba el SDK WEB en native, que
    // viaja sin auth porque firebaseConfig.auth es un Proxy que tira en
    // móvil → todas las queries iban anónimas → Firestore rules (que
    // exigen isSignedIn) rechazaban → listener devolvía vacío → pasajero
    // NUNCA veía choferes en el mapa. Ahora native usa @react-native-firebase
    // que sí hereda el auth del SDK nativo.
    if (Platform.OS !== 'web') {
      let q: any = nativeFirestore().collection('drivers').where('isOnline', '==', true);
      if (vehicleType) q = q.where('vehicleType', '==', vehicleType);
      // TOPE — el mapa solo puede pintar un puñado de carritos; leer la flota
      // entera del país en cada ping GPS de cada chofer es dinero tirado.
      q = q.limit(50);
      return q.onSnapshot((snap: any) => {
        const drivers = snap.docs.map((d: any) => ({ id: d.id, ...d.data() }));
        callback(drivers);
      }, (err: any) => { console.warn('[listenToOnlineDrivers] error:', err?.message); callback([]); });
    }
    const constraints: any[] = [where('isOnline', '==', true)];
    if (vehicleType) constraints.push(where('vehicleType', '==', vehicleType));
    constraints.push(limit(50));
    const q = query(collection(db, 'drivers'), ...constraints);
    return onSnapshot(
      q,
      (snapshot) => {
        const drivers = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        callback(drivers);
      },
      (err) => {
        console.warn('[listenToOnlineDrivers] web error:', err?.message);
        callback([]);
      }
    );
  },

  // --- GPS TRACKING ---
  async updateDriverLocation(driverId: string, lat: number, lng: number) {
    // FIX AUDIT — Dos correcciones históricas que siguen vigentes:
    //  1) NATIVO: antes esto usaba el SDK WEB también en móvil, que viaja sin
    //     auth (db.auth es un Proxy que tira en RN) → las escrituras podían ir
    //     anónimas y las rules rechazarlas. Ahora usa @react-native-firebase en
    //     móvil, igual que setDriverOnline/listenToOnlineDrivers.
    //  2) ESPEJO A /drivers: la posición viva iba SOLO a /driverLocations, pero
    //     el marcador del chofer en el mapa del pasajero y el dispatch
    //     (Cloud Function) leen /drivers.lat/lng — que solo se escribía UNA vez
    //     al ponerse online → marcador congelado + matching por posición vieja.
    //     Por eso el espejo NO se puede quitar: hoy lo leen dispatch.ts,
    //     foodDispatch.ts y eta.ts, y el mapa del pasajero por
    //     listenToOnlineDrivers.
    //
    // FIX P0 (causa raíz #3) — POR QUÉ el espejo ahora va GRUESO y RACIONADO:
    // /drivers es una colección que TODO el que tenga sesión escucha
    // (`where isOnline == true`, 50 docs). Escribir ahí en cada latido de GPS
    // convertía el movimiento de un solo chofer en un snapshot de 50
    // documentos entregado a CADA pasajero con el mapa abierto → 50 marcadores
    // re-renderizados varias veces por segundo → ANR sin traza. El espejo era
    // el motor que disparaba la tormenta.
    //
    // Criterio: el mapa del pasajero necesita ver que HAY CARROS CERCA, no la
    // flota entera con precisión de metro. Así que a /drivers va la CELDA de
    // ~110 m (3 decimales) y solo cuando esa celda cambia de verdad y ha
    // pasado el racionado mínimo. La posición EXACTA sigue yendo íntegra a
    // /driverLocations en cada latido, que es de donde la lee el pasajero
    // asignado (listenToDriverLocation) y donde la regla anti-acoso sí manda.
    // Efecto colateral buscado: el racionado es un TECHO, así que cuando se
    // destape el GPS de alta precisión (causa raíz #10, que hoy hace que los
    // latidos vengan cada 15 s en vez de cada 5) la carga sobre /drivers NO
    // se triplica.
    const exacta = { lat, lng, updatedAt: selloServidor() };
    const espejo = espejoGruesoParaDrivers(driverId, lat, lng);

    const escrituras: Array<Promise<any>> =
      Platform.OS !== 'web'
        ? [
            nativeFirestore().collection('driverLocations').doc(driverId).set(exacta, { merge: true }),
            ...(espejo
              ? [nativeFirestore().collection('drivers').doc(driverId).set(espejo.datos, { merge: true })]
              : []),
          ]
        : [
            setDoc(doc(db, 'driverLocations', driverId), exacta, { merge: true }),
            ...(espejo ? [setDoc(doc(db, 'drivers', driverId), espejo.datos, { merge: true })] : []),
          ];

    try {
      await Promise.all(escrituras);
    } catch (e) {
      // Si el espejo no llegó, hay que olvidar que lo dimos por escrito: si no,
      // el carrito de este chofer se quedaría congelado en el mapa del pasajero
      // hasta que cruzara otra celda. El racionado no puede convertirse en
      // "escribí una vez y ya".
      espejo?.revertir();
      throw e;
    }
  },

  listenToDriverLocation(driverId: string, callback: (location: {lat: number, lng: number} | null) => void) {
    // Fix mismo bug que listenToOnlineDrivers — SDK nativo en mobile.
    if (Platform.OS !== 'web') {
      return nativeFirestore()
        .collection('driverLocations')
        .doc(driverId)
        .onSnapshot((doc: any) => {
          if (doc.exists()) {
            const data = doc.data();
            callback({ lat: data.lat, lng: data.lng });
          } else {
            callback(null);
          }
        }, (err: any) => { console.warn('[listenToDriverLocation] error:', err?.message); callback(null); });
    }
    const driverRef = doc(db, 'driverLocations', driverId);
    return onSnapshot(
      driverRef,
      (doc) => {
        if (doc.exists()) {
          const data = doc.data();
          callback({ lat: data.lat, lng: data.lng });
        } else {
          callback(null);
        }
      },
      (err) => {
        console.warn('[listenToDriverLocation] web error:', err?.message);
        callback(null);
      }
    );
  },

  /**
   * Marca un ride como CANCELLED desde el pasajero. Usa SDK nativo en
   * móvil porque el web va anónimo para users REST → permission-denied.
   * No borra el doc para mantener histórico de auditoría.
   */
  async cancelRideAsPassenger(rideId: string) {
    if (Platform.OS !== 'web') {
      await nativeFirestore()
        .collection('rides')
        .doc(rideId)
        .update({
          status: 'CANCELLED',
          cancelledAt: nativeFirestore.FieldValue.serverTimestamp(),
          cancelledBy: 'PASSENGER',
        });
      return;
    }
    await updateDoc(doc(db, 'rides', rideId), {
      status: 'CANCELLED',
      cancelledAt: serverTimestamp(),
      cancelledBy: 'PASSENGER',
    });
  },

  /**
   * LA PASAJERA RECHAZA LA OFERTA DE UN CHOFER.
   *
   * Hasta la 1.0.12 el rechazo vivía SOLO en la memoria del teléfono de la
   * pasajera: se quitaba la tarjeta de la lista y ya. Dos daños medidos en el
   * viaje LvrCVZjnl… del 19-sep:
   *   · el chofer no se enteraba de nada — su pantalla se quedaba en "Oferta
   *     enviada — esperando respuesta del pasajero" hasta que el cron expiraba
   *     la solicitud diez minutos después;
   *   · el siguiente snapshot de la subcolección `offers` devolvía la oferta
   *     rechazada a la pantalla, porque nada decía que estaba descartada.
   *
   * El rechazo se escribe en el VIAJE, no en la oferta: el chofer ya recibe el
   * documento del viaje entero en su feed de solicitudes pendientes, así que se
   * entera sin un listener nuevo. `arrayUnion` es idempotente: rechazar dos
   * veces al mismo chofer no duplica nada.
   *
   * El `status` no se toca a propósito: la solicitud sigue viva para los demás
   * choferes. Rechazar a uno no es cancelar el viaje.
   */
  async rejectDriverOffer(rideId: string, driverId: string) {
    if (!rideId || !driverId) return;
    if (Platform.OS !== 'web') {
      await nativeFirestore()
        .collection('rides')
        .doc(rideId)
        .update({
          rechazados: nativeFirestore.FieldValue.arrayUnion(driverId),
          updatedAt: nativeFirestore.FieldValue.serverTimestamp(),
        });
      return;
    }
    await updateDoc(doc(db, 'rides', rideId), {
      rechazados: arrayUnion(driverId),
      updatedAt: serverTimestamp(),
    });
  },

  // --- MOTOR DE EMPAREJAMIENTO (VIAJES Y DELIVERY) ---
  async requestRide(passengerId: string, rideData: any) {
    // ── UN PASAJERO, UN VIAJE A LA VEZ (bloque K) ─────────────────────────
    // La limpieza de abajo cancela los PENDING previos, pero un viaje ya
    // ACEPTADO no es basura: hay un chofer en camino. Si el pasajero pedía
    // otro, quedaban dos viajes vivos con el mismo pasajero — un chofer
    // manejando hacia alguien que ya se montó en otro carro, y ningún dato en
    // la app que dijera cuál de los dos era el bueno.
    //
    // Un viaje ABANDONADO (>4h sin actividad) no cuenta: se auto-expira y se
    // deja pasar. Si no, un viaje colgado de una prueba dejaría al pasajero
    // sin poder pedir transporte nunca más.
    try {
      const ESTADOS_VIVOS = ['ACCEPTED', 'ARRIVED', 'IN_PROGRESS'];
      let vivos: any[] = [];
      if (Platform.OS !== 'web') {
        const snap = await nativeFirestore()
          .collection('rides')
          .where('passengerId', '==', passengerId)
          .where('status', 'in', ESTADOS_VIVOS)
          .limit(5)
          .get();
        vivos = snap.docs.map((d: any) => ({ id: d.id, ...d.data() }));
      } else {
        const q = query(
          collection(db, 'rides'),
          where('passengerId', '==', passengerId),
          where('status', 'in', ESTADOS_VIVOS),
          limit(5)
        );
        const snap = await getDocs(q);
        vivos = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      }
      const enCurso = vivos.filter((r) => {
        if (!isRideFresh(r, false)) {
          autoExpireStaleRide(r.id);
          return false;
        }
        // ── H58 (auditoría 16-sep-2026): EL CHOFER QUE OLVIDA "COMPLETAR" ────
        // Caso de todos los días: el viaje termina, el chofer no toca
        // "Completar" y se va. El viaje queda IN_PROGRESS y el servidor no lo
        // cierra (su cron solo mira ACCEPTED/ARRIVED, a propósito). Con la
        // ventana de 4 h, el pasajero que llegó bien se quedaba hasta 4 horas
        // sin poder pedir otro carro, y la única salida era cancelar… con
        // multa por cancelar un viaje en curso.
        //
        // Un IN_PROGRESS sin actividad en 90 min ya NO bloquea. Solo se deja
        // pasar: aquí NO se auto-expira, por dos razones:
        //   · un viaje largo de verdad (a Santiago, a Punta Cana) supera los
        //     90 min y no se le puede cancelar a nadie en plena carretera;
        //   · las reglas solo aceptan la limpieza sin firma a las 3 h
        //     (`viajeAbandonado()`), así que la escritura se denegaría igual.
        // Cerrar esos viajes es trabajo del barrido del servidor.
        if (
          String(r.status ?? '') === 'IN_PROGRESS' &&
          !isRideFresh(r, false, VENTANA_CANDADO_EN_CURSO_MS)
        ) {
          console.warn('[requestRide] IN_PROGRESS sin actividad >90 min, no bloquea:', r.id);
          return false;
        }
        return true;
      });
      if (enCurso.length > 0) {
        // ── CONTRATO CON LA PANTALLA — NO RENOMBRAR ─────────────────────────
        // Esto NO es un fallo de red ni un rechazo del servidor: es una
        // decisión nuestra, y el pasajero tiene que leer POR QUÉ. La pantalla
        // (map.tsx) distingue este caso por `error.code === 'ride/active-exists'`
        // y muestra `error.message` tal cual, que ya está escrito para una
        // persona. `rideId` va también para poder llevarla a su viaje vivo.
        // Si se cambia cualquiera de los tres nombres, el aviso vuelve a ser el
        // genérico de "revisa tu conexión", que es mentira.
        const err: any = new Error(
          'Ya tienes un viaje en curso. Termínalo o cancélalo antes de pedir otro.'
        );
        err.code = 'ride/active-exists';
        err.rideId = enCurso[0].id;
        throw err;
      }
    } catch (e: any) {
      // Solo el bloqueo intencionado corta el flujo. Si la consulta falló
      // (índice, red), NO dejamos a nadie a pie: se sigue adelante, y el
      // servidor es quien tiene la última palabra — `frenarSolicitudEnCadena`
      // (functions/src/dispatch.ts) repite esta comprobación con permisos de
      // administrador antes de avisar a ningún chofer, y cancela el viaje nuevo
      // con `cancelReason: 'duplicado_viaje_en_curso'` si el pasajero ya iba en
      // otro. Por eso aquí se puede fallar hacia el lado amable sin que quede
      // ningún duplicado vivo.
      if (e?.code === 'ride/active-exists') throw e;
      console.warn('[requestRide] no se pudo comprobar viaje en curso:', e?.message);
    }

    // FIX FANTASMAS DUPLICADOS — Antes si el pasajero tocaba "Buscar viaje"
    // varias veces (o cerraba la app y volvía), creábamos múltiples rides
    // PENDING para el mismo passenger. Cada uno aparecía como card separada
    // en el feed del chofer. Aquí cancelamos cualquier PENDING previo del
    // mismo pasajero antes de crear el nuevo. Idempotente: si no hay
    // previos, no hace nada.
    try {
      if (Platform.OS !== 'web') {
        const prevSnap = await nativeFirestore()
          .collection('rides')
          .where('passengerId', '==', passengerId)
          .where('status', '==', 'PENDING')
          .limit(10)
          .get();
        if (prevSnap.size > 0) {
          const batch = nativeFirestore().batch();
          prevSnap.docs.forEach((d: any) => {
            batch.update(d.ref, {
              status: 'CANCELLED',
              cancelledAt: nativeFirestore.FieldValue.serverTimestamp(),
              cancelledBy: 'PASSENGER',
              cancelReason: 'auto_new_request',
            });
          });
          await batch.commit();
        }
      } else {
        const prevQuery = query(
          collection(db, 'rides'),
          where('passengerId', '==', passengerId),
          where('status', '==', 'PENDING')
        );
        const prevSnap = await getDocs(prevQuery);
        for (const d of prevSnap.docs) {
          await updateDoc(d.ref, {
            status: 'CANCELLED',
            cancelledAt: serverTimestamp(),
            cancelledBy: 'PASSENGER',
            cancelReason: 'auto_new_request',
          });
        }
      }
    } catch (e: any) {
      console.warn('[requestRide] cleanup previo falló (no crítico):', e?.message);
    }

    // En móvil usamos el SDK nativo de Firestore — el web va anónimo cuando
    // el user vino por el fallback REST (sin nativo session). El custom
    // token exchange en authBridge ya estableció la sesión nativa, así que
    // nativeFirestore() hereda el auth y las security rules aceptan el
    // create de /rides/{rideId}.
    // PIN DE VIAJE (Uber-style) — Código de 4 dígitos que el pasajero le da
    // al chofer para iniciar el viaje. Garantiza que el chofer recoja al
    // pasajero correcto y no arranque (ni cobre) antes de que aborde. Se
    // genera aquí, se muestra al pasajero, y el chofer debe ingresarlo para
    // pasar ARRIVED→IN_PROGRESS. Va DESPUÉS de ...rideData para que sea
    // autoritativo (ningún caller puede sobrescribirlo).
    // ⚠️ FIX DE AUDITORÍA — El PIN ya NO va en el doc del viaje. Ahí el CHOFER
    // podía leerlo (tiene permiso de lectura del ride) y escribirlo él mismo
    // sin pedírselo nunca al pasajero: la verificación de recogida era puro
    // teatro. Ahora vive en /rides/{id}/private/security, que SOLO el pasajero
    // puede leer, y lo valida la Cloud Function `startRideWithPin`.
    const ridePin = String(Math.floor(1000 + Math.random() * 9000));

    // H63 — EL CÓDIGO DE ENTREGA DEL PAQUETE.
    // En un viaje normal el candado es el PIN de recogida. En un ENVÍO no hay
    // nadie que se monte: el chofer sale solo con la caja, y hasta ahora el
    // envío se cerraba con un «sí» en su propio teléfono. Este segundo código
    // lo dicta QUIEN RECIBE el paquete, y lo verifica el servidor
    // (`completarEnvioConPin`). Vive en el mismo documento privado que el otro,
    // que solo puede leer quien pidió el viaje: es el remitente quien se lo
    // pasa a la persona que va a recibir.
    const esEnvioDePaquete = String(rideData?.serviceType ?? '') === 'DELIVERY';
    const pinEntrega = esEnvioDePaquete
      ? String(Math.floor(1000 + Math.random() * 9000))
      : null;
    const datosDeSeguridad = pinEntrega
      ? { ridePin, pinFailedAttempts: 0, pinEntrega, entregaFailedAttempts: 0 }
      : { ridePin, pinFailedAttempts: 0 };

    // ZONA DE BÚSQUEDA — celda geográfica del punto de recogida. Es lo que
    // permite que cada chofer vea SOLO los viajes de su área en vez de los de
    // todo el país. Se calcula aquí (aritmética pura, sin llamadas de red) y va
    // DESPUÉS de ...rideData para que ningún caller pueda falsearla.
    const oLat = Number(rideData?.origin_lat ?? rideData?.originLat);
    const oLng = Number(rideData?.origin_lng ?? rideData?.originLng);
    const zone = Number.isFinite(oLat) && Number.isFinite(oLng) ? zonaDe(oLat, oLng) : null;

    // ── BLOQUE J — UN VIAJE SIN PIN NO PUEDE QUEDAR SUELTO ────────────────
    // El viaje y su PIN son DOS escrituras: primero el documento del viaje,
    // después la subcolección privada con el código. Si la segunda falla (red
    // caída justo ahí, permiso denegado), quedaba un viaje PENDING que ningún
    // pasajero podía enseñar y que el servidor arrancaba sin validar nada — la
    // verificación de recogida desaparecía en silencio para ese viaje.
    //
    // Aquí el viaje recién creado se CANCELA si su PIN no llegó a guardarse, y
    // el error sube a la pantalla: mejor que el pasajero lo vuelva a pedir a
    // que se monte en un viaje sin candado. No se borra porque las reglas de
    // /rides prohíben el delete (allow delete: if false); cancelar sí se puede
    // y lo saca del feed de los choferes igual.
    const anularViajeSinPin = async (rideId: string) => {
      try {
        const payload = {
          status: 'CANCELLED',
          cancelledAt: (Platform.OS !== 'web'
            ? nativeFirestore.FieldValue.serverTimestamp()
            : serverTimestamp()),
          cancelledBy: 'PASSENGER',
          cancelReason: 'pin_write_failed',
        };
        if (Platform.OS !== 'web') {
          await nativeFirestore().collection('rides').doc(rideId).update(payload);
        } else {
          await updateDoc(doc(db, 'rides', rideId), payload);
        }
      } catch (e2: any) {
        // Ya no queda nada que hacer desde el cliente. Se deja el rastro para
        // que el viaje huérfano se pueda identificar en el backend.
        console.warn('[requestRide] viaje sin PIN que NO se pudo cancelar:', rideId, e2?.message);
      }
    };

    if (Platform.OS !== 'web') {
      const ref = await nativeFirestore()
        .collection('rides')
        .add({
          passengerId,
          ...rideData,
          zone,
          status: 'PENDING',
          createdAt: nativeFirestore.FieldValue.serverTimestamp(),
        });
      try {
        await ref.collection('private').doc('security').set(datosDeSeguridad);
      } catch (e: any) {
        console.warn('[requestRide] el PIN no se guardó, se anula el viaje:', ref.id, e?.message);
        await anularViajeSinPin(ref.id);
        throw e;
      }
      return ref;
    }
    const ref = await addDoc(collection(db, 'rides'), {
      passengerId,
      ...rideData,
      zone,
      status: 'PENDING',
      createdAt: serverTimestamp(),
    });
    try {
      await setDoc(doc(db, `rides/${ref.id}/private/security`), datosDeSeguridad);
    } catch (e: any) {
      console.warn('[requestRide] el PIN no se guardó, se anula el viaje:', ref.id, e?.message);
      await anularViajeSinPin(ref.id);
      throw e;
    }
    return ref;
  },

  /**
   * El PASAJERO lee su propio PIN desde la subcolección privada (el chofer no
   * puede: ver la regla /rides/{rideId}/private/{doc}). Se lo muestra para que
   * se lo dicte al chofer al abordar.
   */
  listenToRidePin(
    rideId: string,
    // H63 — el segundo argumento es el código de ENTREGA del paquete (solo en
    // envíos). Va por el mismo camino y el mismo documento privado para no
    // abrir un segundo listener sobre el mismo doc; quien no lo necesite
    // sencillamente no lo recibe.
    callback: (pin: string | null, pinEntrega?: string | null) => void
  ) {
    const emit = (d: any) =>
      callback(
        d?.ridePin != null ? String(d.ridePin) : null,
        d?.pinEntrega != null ? String(d.pinEntrega) : null
      );
    if (Platform.OS !== 'web') {
      return nativeFirestore()
        .collection('rides').doc(rideId).collection('private').doc('security')
        .onSnapshot((snap: any) => emit(snap?.exists?.() ? snap.data() : null),
                    (err: any) => { console.warn('[listenToRidePin]', err?.message); callback(null); });
    }
    return onSnapshot(
      doc(db, `rides/${rideId}/private/security`),
      (snap) => emit(snap.exists() ? snap.data() : null),
      (err) => {
        console.warn('[listenToRidePin] web error:', err?.message);
        emit(null);
      }
    );
  },

  /**
   * El alta de LOGÍSTICA de quien llama (curier, mudanza, carga, gira).
   *
   * Vive en su propia colección, `proveedores_logistica/{uid}`, y no en /users
   * ni en /drivers a propósito: el emparejamiento de viajes es una consulta
   * sobre `drivers` filtrando `vehicleType`, y meter aquí un tipo nuevo haría
   * que al motorista le llegaran solicitudes de mudanza. El fundador fue
   * explícito: «nada de eso se toca».
   *
   * Devuelve `null` mientras no exista — que es el caso normal: casi nadie
   * tiene un camión.
   */
  listenToProveedorLogistica(uid: string, callback: (datos: any | null) => void) {
    const emit = (d: any) => callback(d ?? null);
    if (Platform.OS !== 'web') {
      return nativeFirestore()
        .collection('proveedores_logistica').doc(uid)
        .onSnapshot(
          (snap: any) => emit(snap?.exists?.() ? snap.data() : null),
          (err: any) => {
            // Se emite `null`, no se deja la pantalla girando: un error de
            // permisos aquí significa «todavía no te has dado de alta» para
            // quien mira, y una ruleta eterna parece que la app se coló.
            console.warn('[listenToProveedorLogistica]', err?.message);
            emit(null);
          }
        );
    }
    return onSnapshot(
      doc(db, `proveedores_logistica/${uid}`),
      (snap) => emit(snap.exists() ? snap.data() : null),
      (err) => {
        console.warn('[listenToProveedorLogistica] web error:', err?.message);
        emit(null);
      }
    );
  },

  /**
   * Los trabajos de logística de quien llama, en el papel que se le diga.
   *
   * `papel: 'CLIENTE'` consulta `where('clienteUid','==',uid)` y
   * `'PROVEEDOR'` consulta `where('proveedorUid','==',uid)`. Las dos son
   * consultas LEGALES porque todo documento que devuelven cumple la regla de
   * `servicios_logistica` — que a propósito NO tiene rama para los trabajos
   * abiertos: eso sería servir la dirección de casa de cada cliente. El
   * escaparate de los trabajos abiertos va por la callable
   * `escaparateDeLogistica`, que proyecta campos.
   */
  listenToServiciosDeLogistica(
    uid: string,
    papel: 'CLIENTE' | 'PROVEEDOR',
    callback: (servicios: any[]) => void
  ) {
    const campo = papel === 'CLIENTE' ? 'clienteUid' : 'proveedorUid';
    // El `orderBy` va en la CONSULTA, no solo aquí. Sin él, el `limit(50)` lo
    // aplica el servidor sobre un orden por `__name__` — y los IDs los genera
    // `.add()`, o sea que son aleatorios. Con más de 50 trabajos, el que tenía
    // EN_CURSO podía quedarse FUERA de los 50 que llegan, y entonces no había
    // forma de meterle el PIN: el trabajo quedaba imposible de cerrar. Este
    // `sort` ordena lo que ya llegó; no cambia cuál llegó.
    const ordenar = (lista: any[]) =>
      lista.sort((a, b) => Number(b.creadoMs ?? 0) - Number(a.creadoMs ?? 0));

    if (Platform.OS !== 'web') {
      return nativeFirestore()
        .collection('servicios_logistica')
        .where(campo, '==', uid)
        .orderBy('creadoMs', 'desc')
        .limit(50)
        .onSnapshot(
          (snap: any) => callback(ordenar((snap?.docs ?? []).map((d: any) => ({ id: d.id, ...d.data() })))),
          (err: any) => {
            console.warn('[listenToServiciosDeLogistica]', err?.message);
            callback([]);
          }
        );
    }
    return onSnapshot(
      query(
        collection(db, 'servicios_logistica'),
        where(campo, '==', uid),
        orderBy('creadoMs', 'desc'),
        limit(50)
      ),
      (snap) => callback(ordenar(snap.docs.map((d) => ({ id: d.id, ...d.data() })))),
      (err) => {
        console.warn('[listenToServiciosDeLogistica] web error:', err?.message);
        callback([]);
      }
    );
  },

  /**
   * LA COLA DE ALTAS DE LOGÍSTICA POR REVISAR. Solo para el admin.
   *
   * La regla de `proveedores_logistica` deja leer al dúeño y al admin con 2FA,
   * así que esta consulta solo devuelve algo a un admin — a cualquier otro le
   * responde permission-denied, y el callback emite una lista vacía.
   *
   * Orden ASCENDENTE por `creadoMs`: el que lleva más tiempo esperando va
   * primero. Es lo justo y además es lo que evita que alguien se quede
   * olvidado al fondo de la cola para siempre.
   */
  listenToColaDeLogistica(callback: (altas: any[]) => void) {
    const mapear = (docs: any[]) => docs.map((d: any) => ({ id: d.id, ...d.data() }));
    if (Platform.OS !== 'web') {
      return nativeFirestore()
        .collection('proveedores_logistica')
        .where('estado', '==', 'PENDIENTE')
        .orderBy('creadoMs', 'asc')
        .limit(50)
        .onSnapshot(
          (snap: any) => callback(mapear(snap?.docs ?? [])),
          (err: any) => {
            console.warn('[listenToColaDeLogistica]', err?.message);
            callback([]);
          }
        );
    }
    return onSnapshot(
      query(
        collection(db, 'proveedores_logistica'),
        where('estado', '==', 'PENDIENTE'),
        orderBy('creadoMs', 'asc'),
        limit(50)
      ),
      (snap) => callback(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => {
        console.warn('[listenToColaDeLogistica] web error:', err?.message);
        callback([]);
      }
    );
  },

  /**
   * El PIN de entrega de un trabajo de logística. **Solo lo lee el CLIENTE**:
   * la regla compara el `clienteUid` que lleva el propio documento del PIN.
   *
   * Si lo pudiera leer el proveedor, se autoentregaría el trabajo sin
   * presentarse y la comisión se devengaría sola — es el mismo agujero que se
   * cerró en los pedidos de comida.
   */
  listenToPinDeLogistica(servicioId: string, callback: (pin: string | null) => void) {
    const emit = (d: any) => callback(d?.pin != null ? String(d.pin) : null);
    if (Platform.OS !== 'web') {
      return nativeFirestore()
        .collection('servicios_logistica').doc(servicioId).collection('private').doc('security')
        .onSnapshot(
          (snap: any) => emit(snap?.exists?.() ? snap.data() : null),
          (err: any) => { console.warn('[listenToPinDeLogistica]', err?.message); callback(null); }
        );
    }
    return onSnapshot(
      doc(db, `servicios_logistica/${servicioId}/private/security`),
      (snap) => emit(snap.exists() ? snap.data() : null),
      (err) => {
        console.warn('[listenToPinDeLogistica] web error:', err?.message);
        callback(null);
      }
    );
  },

  /**
   * Viajes disponibles PARA ESTE CHOFER, ordenados por cercanía.
   *
   * ANTES: traía los PENDING de TODO EL PAÍS y se quedaba con los 10 más
   * recientes. Un chofer en el Distrito Nacional podía ver la lista llena de
   * solicitudes de Santiago y NUNCA llegar a la que tenía a dos cuadras. Así se
   * pierden viajes por ambos lados — el pasajero cree que no hay choferes y el
   * chofer cree que no hay pasajeros.
   *
   * AHORA: se consulta la celda geográfica del chofer y sus 8 vecinas (~66 km
   * de lado, un distrito completo con holgura), se calcula la distancia real a
   * cada solicitud, se descartan las que quedan fuera del radio y se ordena de
   * la más cercana a la más lejana. Cuando el chofer se mueve a otra zona, la
   * pantalla vuelve a suscribirse y le aparecen los viajes de la zona nueva.
   *
   * `origen` opcional: sin él se mantiene el comportamiento anterior, para no
   * dejar al chofer sin viajes mientras el GPS todavía no ha respondido.
   */
  listenToPendingRides(
    callback: (rides: any[]) => void,
    origen?: { lat: number; lng: number; radioKm?: number }
  ) {
    //
    // FIX DEMOS FANTASMA — Antes el listener traía TODOS los PENDING sin
    // límite ni filtro de edad → choferes veían rides huérfanos de tests
    // viejos como si fueran solicitudes reales actuales. Resultado en
    // captura del founder: 3 cards "Pasajero $200" con datos vacíos.
    //
    // Ahora:
    //   - Filtramos en memoria los rides creados hace > 10 min (stale).
    //   - Ordenamos por createdAt desc para mostrar lo más reciente arriba.
    //   - Limitamos a 10 cards visibles (el chofer no procesa más de uno
    //     a la vez; mostrar más es ruido). El cron expireStalePendingRides
    //     server-side cierra los viejos de forma definitiva en background.
    const STALE_MS = 10 * 60 * 1000;
    const MAX_CARDS = 10;
    // ── RADIO ELÁSTICO ───────────────────────────────────────────────────
    // Antes era un solo número: 25 km fijos. Eso es medio país en el feed de un
    // chofer del Distrito Nacional, y es exactamente la queja del fundador
    // («gastaría gasolina solo para ir a recogerlo»).
    //
    // Pero cortar en seco a 5 km es peor de otra forma: en hora floja, o en una
    // ciudad con menos densidad, el chofer abre la app y no ve NADA. Un chofer
    // que ve la pantalla vacía tres veces deja de abrirla, y eso cuesta más que
    // una recogida larga.
    //
    // Así que no se corta: se PRIORIZA. Primero lo que está dentro del radio
    // preferido; si eso no llena ni el mínimo de tarjetas, se completa con lo
    // siguiente más cercano, marcado como lejos para que el chofer lo vea
    // escrito y decida él. Nunca más allá del tope duro.
    const RADIO_PREFERIDO_KM = origen?.radioKm ?? 6;
    const RADIO_TOPE_KM = Math.max(RADIO_PREFERIDO_KM, 25);
    const MINIMO_TARJETAS = 3;

    const cleanAndEmit = (rides: any[]) => {
      const cutoff = Date.now() - STALE_MS;
      const conDistancia = rides
        .filter((r) => {
          const created = r.createdAt?.toMillis?.()
            ?? r.createdAt?.toDate?.()?.getTime?.()
            ?? r.created_at?.toMillis?.()
            ?? 0;
          // V13 — una solicitud que el servidor REABRIÓ (el chofer aceptado ya
          // iba en otro viaje) conserva su createdAt viejo. Cuenta desde la
          // reapertura, igual que el cron que la expira: si no, desaparecía del
          // feed mientras a la pasajera se le dice que espere nuevas ofertas.
          const reabierto = r.reabiertoPorServidorEn?.toMillis?.() ?? 0;
          // Si createdAt es null o desconocido, lo descartamos en producción.
          // Mejor perderse uno que mostrar fantasmas.
          return Math.max(created, reabierto) > cutoff;
        })
        .map((r) => {
          if (!origen) return { ...r, distanceKm: null };
          const lat = Number(r.origin_lat ?? r.originLat);
          const lng = Number(r.origin_lng ?? r.originLng);
          if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
            // Sin coordenadas no se puede medir. Se deja pasar en vez de
            // ocultarlo: perder un viaje real es peor que mostrar uno sin km.
            return { ...r, distanceKm: null };
          }
          return { ...r, distanceKm: haversineKm({ lat: origen.lat, lng: origen.lng }, { lat, lng }) };
        })
        .filter((r) => r.distanceKm === null || r.distanceKm <= RADIO_TOPE_KM)
        .sort((a, b) => {
          // Primero lo más CERCA (es lo que el chofer puede tomar de verdad);
          // entre dos a la misma distancia, lo más reciente.
          if (a.distanceKm !== null && b.distanceKm !== null && a.distanceKm !== b.distanceKm) {
            return a.distanceKm - b.distanceKm;
          }
          const ta = a.createdAt?.toMillis?.() ?? 0;
          const tb = b.createdAt?.toMillis?.() ?? 0;
          return tb - ta;
        });

      // Un viaje sin coordenadas (distanceKm null) cuenta como cercano: no se
      // le puede medir y es peor perderlo que enseñarlo.
      const cerca = conDistancia.filter((r) => r.distanceKm === null || r.distanceKm <= RADIO_PREFERIDO_KM);
      const lejos = conDistancia.filter((r) => r.distanceKm !== null && r.distanceKm > RADIO_PREFERIDO_KM);
      const elegidos =
        cerca.length >= MINIMO_TARJETAS
          ? cerca.slice(0, MAX_CARDS)
          : [...cerca, ...lejos].slice(0, Math.max(MINIMO_TARJETAS, cerca.length));

      // `lejos: true` es para que la tarjeta lo diga con todas sus letras. Un
      // viaje lejano enseñado sin avisar es justo lo que molesta del otro.
      callback(
        elegidos.map((r) => ({
          ...r,
          lejos: r.distanceKm !== null && r.distanceKm > RADIO_PREFERIDO_KM,
        }))
      );
    };

    // Celda del chofer + las 8 vecinas (~66 km de lado). Firestore admite
    // hasta 30 valores en un `in`; usamos 9.
    const zonas = origen ? zonasVecinas(origen.lat, origen.lng) : null;

    if (Platform.OS !== 'web') {
      let q: any = nativeFirestore().collection('rides').where('status', '==', 'PENDING');
      if (zonas) q = q.where('zone', 'in', zonas);
      return q.onSnapshot((snap: any) => {
        const rides = snap.docs.map((d: any) => ({ id: d.id, ...d.data() }));
        cleanAndEmit(rides);
      }, (err: any) => { console.warn('[listenToPendingRides] error:', err?.message); callback([]); });
    }
    const constraints: any[] = [where('status', '==', 'PENDING')];
    if (zonas) constraints.push(where('zone', 'in', zonas));
    const q = query(collection(db, 'rides'), ...constraints);
    return onSnapshot(
      q,
      (snapshot) => {
        const rides = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        cleanAndEmit(rides);
      },
      (err) => {
        console.warn('[listenToPendingRides] web error:', err?.message);
        callback([]);
      }
    );
  },

  listenToRideOffers(rideId: string, callback: (offers: any[]) => void) {
    // 2. Pasajero escucha subcolección de ofertas de choferes
    // FIX DISCOVERY — SDK nativo en mobile.
    if (Platform.OS !== 'web') {
      return nativeFirestore()
        .collection('rides')
        .doc(rideId)
        .collection('offers')
        .onSnapshot((snap: any) => {
          const offers = snap.docs.map((d: any) => ({ id: d.id, ...d.data() }));
          callback(offers);
        }, (err: any) => { console.warn('[listenToRideOffers] error:', err?.message); callback([]); });
    }
    const offersRef = collection(db, `rides/${rideId}/offers`);
    return onSnapshot(
      offersRef,
      (snapshot) => {
        const offers = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        callback(offers);
      },
      (err) => {
        console.warn('[listenToRideOffers] web error:', err?.message);
        callback([]);
      }
    );
  },

  async sendRideOffer(rideId: string, driverId: string, offerData: any) {
    // FIX CRÍTICO CONTRAOFERTA — Antes este método usaba el SDK web aún
    // en móvil. Cuando el chofer se autenticó vía REST (Play Integrity sin
    // token), el SDK web va anónimo y las security rules
    // rechazan el create de /rides/{id}/offers → la contraoferta NUNCA
    // se escribía → el pasajero NUNCA la veía en su pantalla. El listener
    // `listenToRideOffers` ya está en SDK nativo, pero el WRITE no estaba.
    // Ahora con el SDK nativo la sesión se hereda del custom token
    // exchange (authBridge) y la rule pasa.
    if (Platform.OS !== 'web') {
      return await nativeFirestore()
        .collection('rides')
        .doc(rideId)
        .collection('offers')
        .add({
          driverId,
          ...offerData,
          createdAt: nativeFirestore.FieldValue.serverTimestamp(),
        });
    }
    const offersRef = collection(db, `rides/${rideId}/offers`);
    return await addDoc(offersRef, {
      driverId,
      ...offerData,
      createdAt: serverTimestamp()
    });
  },

  /**
   * ¿ESTE CHOFER TIENE UNA OFERTA SUYA EN ESE VIAJE? — H22 (auditoría 16-sep-2026)
   *
   * La pantalla del chofer solo montaba un viaje aceptado si su id coincidía con
   * UNA casilla local (`pendingOfferRideId`), y esa casilla la pisa cada oferta
   * nueva. Un chofer que ofertaba a A y luego a B, al ser aceptado en A veía A
   * descartado como "fantasma": él esperando respuesta, el pasajero con "tu chofer
   * va en camino" hasta 2 horas.
   *
   * Esto pregunta al SERVIDOR en vez de a la casilla, con el mismo ancla que usa
   * `elChoferOfertoDeVerdad` (functions/src/rides.ts): una oferta de
   * `rides/{id}/offers` solo la puede crear el chofer que la firma, así que es la
   * prueba de que ese viaje es suyo por voluntad propia y no un resto de una
   * prueba ni un `driverId` puesto por otro.
   *
   * La regla de lectura de /offers ya deja leer al chofer sus propias ofertas
   * (`resource.data.driverId == request.auth.uid`). Mismo orden que el servidor:
   *   1. La oferta ACEPTADA (`acceptedOfferId`, que `acceptRideOffer` escribe en
   *      la misma transacción que el `driverId`): una lectura de UN documento.
   *      Si es mía, listo. Si la regla la deniega es que no es mía (o no existe)
   *      y se sigue al paso 2: un denegado aquí no es respuesta.
   *   2. Respaldo: cualquier oferta mía en ese viaje (`where` + `limit(1)`; el
   *      `where` es lo que le deja a Firestore probar la regla en la consulta).
   *
   * LANZA si la consulta del paso 2 falla: quien llama tiene que poder distinguir
   * "no hay oferta mía" de "no lo pude comprobar", porque solo lo primero es un
   * fantasma. Nunca devuelve `false` por un error.
   */
  async tengoOfertaEnViaje(rideId: string, driverId: string, acceptedOfferId?: string | null): Promise<boolean> {
    if (!rideId || !driverId) return false;
    const ofertaAceptada = String(acceptedOfferId ?? '').trim();
    if (ofertaAceptada) {
      try {
        let datos: any = null;
        if (Platform.OS !== 'web') {
          const s: any = await nativeFirestore()
            .collection('rides')
            .doc(rideId)
            .collection('offers')
            .doc(ofertaAceptada)
            .get();
          datos = s.exists() ? s.data() : null;
        } else {
          const s = await getDoc(doc(db, `rides/${rideId}/offers/${ofertaAceptada}`));
          datos = s.exists() ? s.data() : null;
        }
        if (datos && String(datos.driverId ?? '').trim() === driverId) return true;
      } catch (e: any) {
        if (!String(e?.code ?? e?.message ?? '').includes('permission-denied')) throw e;
      }
    }
    if (Platform.OS !== 'web') {
      const snap = await nativeFirestore()
        .collection('rides')
        .doc(rideId)
        .collection('offers')
        .where('driverId', '==', driverId)
        .limit(1)
        .get();
      return !snap.empty;
    }
    const snap = await getDocs(
      query(collection(db, `rides/${rideId}/offers`), where('driverId', '==', driverId), limit(1))
    );
    return !snap.empty;
  },

  async acceptRideOffer(rideId: string, offerId: string, driverId: string, offerData?: any) {
    // FIX A-3 — Aceptación TRANSACCIONAL. Antes era un update simple: si el
    // pasajero tocaba dos ofertas casi a la vez, o el viaje ya estaba
    // cancelado/aceptado, igual se sobreescribía driverId/status (viajes
    // "gemelos", reasignación). Ahora leemos el viaje dentro de la transacción
    // y solo aceptamos si sigue PENDING; si no, lanzamos para que la UI avise.
    //
    // FIX FOTO — Además persistimos en el ride los datos del chofer que trae
    // la oferta (foto, nombre, teléfono, vehículo, placa). Antes SOLO vivían
    // en memoria (selectedDriver=offer); al reabrir la app o reconstruir el
    // estado desde el ride, la foto del chofer se perdía y salía "Conductor"
    // con placeholder.
    const od = offerData || {};
    const driverFields: any = {
      // La cara, solo si el servidor ya selló la oferta con la selfie aprobada;
      // si no, null (congelarDatosDelViaje la pone igual al aceptarse).
      driverPhoto: od.reputacionSource === 'SERVER_TRIGGER' ? (od.driverPhoto ?? null) : null,
      driverName: od.driverName ?? od.userName ?? null,
      driverPhone: od.driverPhone ?? null,
      driverRating: od.rating ?? null,
      vehicleBrand: od.vehicleBrand ?? null,
      vehicleModel: od.vehicleModel ?? null,
      vehicleColor: od.vehicleColor ?? null,
      vehicleInfo: od.vehicleInfo ?? null,
      licensePlate: od.licensePlate ?? null,
    };

    // ── EL SELLO Y EL TIPO DE VEHÍCULO TAMBIÉN SE GUARDAN ─────────────────
    // Al reabrir la app a mitad de viaje, el pasajero no reconstruye al chofer
    // desde la oferta (que ya no está en memoria) sino desde el documento del
    // viaje. Como estos dos datos no se guardaban, al volver se le apagaba el
    // escudo de verificado y le aparecía un carro genérico aunque hubiera
    // pedido un motor: el chofer parecía otro, sin que nada hubiera cambiado.
    //
    // El sello se escribe solo si la oferta trae el dato de verdad. Un `false`
    // por omisión sería decir "este chofer NO está verificado" sin saberlo, y
    // un `true` sería el adorno que ya se quitó de toda la app.
    const selloVerificado =
      typeof od.isFaceIdVerified === 'boolean' ? od.isFaceIdVerified
        : typeof od.isDriverVerified === 'boolean' ? od.isDriverVerified
          : null;
    if (selloVerificado !== null) driverFields.isDriverVerified = selloVerificado;

    // ⚠️ `vehicleType` en el viaje es EL VEHÍCULO QUE PIDIÓ EL PASAJERO —lo
    // escribe requestRide y por él filtra el despacho—. Aquí se confirma con el
    // vehículo real del chofer que aceptó, pero SOLO si la oferta lo trae: el
    // `?? null` de antes borraba el tipo pedido cada vez que la oferta venía sin
    // él, y un viaje pedido en motor se quedaba sin tipo ninguno.
    const tipoDelChofer = typeof od.vehicleType === 'string' ? od.vehicleType.trim() : '';
    if (tipoDelChofer) driverFields.vehicleType = tipoDelChofer;

    // ── EL PRECIO PACTADO NO LO ESCRIBE ESTE TELÉFONO ─────────────────────
    // Aquí se escribía `final_price`, el precio congelado del que salen la
    // multa del pasajero, la compensación al chofer y el cobro al completar.
    // Las reglas lo bloquean para TODO cliente, y hacen bien —el dinero del
    // viaje no puede depender de un campo que escribe una de las dos partes—,
    // pero Firestore deniega la transacción ENTERA cuando un solo campo del
    // update está prohibido: con esta escritura puesta, NADIE PODÍA ACEPTAR
    // UN VIAJE.
    //
    // Y fallaba de la peor manera para diagnosticarlo: cuando el precio de la
    // oferta no se podía leer, el campo no llegaba a escribirse y la
    // aceptación SÍ pasaba. O sea que el fallo parecía intermitente siendo
    // total.
    //
    // Ahora lo congela el SERVIDOR (`congelarDatosDelViaje`, en
    // functions/src/rides.ts): ve el paso a ACCEPTED, lee con Admin SDK el
    // precio de `rides/{id}/offers/{acceptedOfferId}` —que las reglas dejan
    // inmutable en cuanto el viaje deja de buscar chofer— y escribe el número.
    // El Admin SDK no pasa por reglas, así que el campo queda cerrado a los
    // dos teléfonos y aun así se escribe. Lo único que hace falta desde aquí
    // es `acceptedOfferId`, que va abajo en la transacción: es la pista por la
    // que el servidor encuentra el precio exacto que el pasajero aceptó.
    if (Platform.OS !== 'web') {
      await nativeFirestore().runTransaction(async (tx: any) => {
        const rideRef = nativeFirestore().collection('rides').doc(rideId);
        const snap = await tx.get(rideRef);
        if (!snap.exists()) throw new Error('RIDE_NOT_FOUND');
        const data = snap.data();
        if (data?.status !== 'PENDING') throw new Error('RIDE_NOT_PENDING');
        if (data?.driverId) throw new Error('RIDE_ALREADY_ASSIGNED');
        tx.update(rideRef, {
          status: 'ACCEPTED',
          acceptedOfferId: offerId,
          driverId,
          ...driverFields,
          updatedAt: nativeFirestore.FieldValue.serverTimestamp(),
        });
      });
      return;
    }
    const rideRef = doc(db, 'rides', rideId);
    await runTransaction(db, async (tx) => {
      const snap = await tx.get(rideRef);
      if (!snap.exists()) throw new Error('RIDE_NOT_FOUND');
      const data = snap.data() as any;
      if (data?.status !== 'PENDING') throw new Error('RIDE_NOT_PENDING');
      if (data?.driverId) throw new Error('RIDE_ALREADY_ASSIGNED');
      tx.update(rideRef, {
        status: 'ACCEPTED',
        acceptedOfferId: offerId,
        driverId,
        ...driverFields,
        updatedAt: serverTimestamp(),
      });
    });
  },

  // --- DESVÍO DE RUTA (chofer propone → pasajero aprueba/rechaza) ---
  async proposeRouteDeviation(rideId: string, reason: string) {
    const payload = {
      routeDeviation: {
        status: 'PENDING',
        reason: reason || 'Sin especificar',
        requestedBy: 'DRIVER',
        requestedAt: Date.now(),
      },
    };
    if (Platform.OS !== 'web') {
      await nativeFirestore().collection('rides').doc(rideId).set(payload, { merge: true });
      return;
    }
    await setDoc(doc(db, 'rides', rideId), payload, { merge: true });
  },

  async respondRouteDeviation(rideId: string, approved: boolean) {
    const status = approved ? 'APPROVED' : 'REJECTED';
    if (Platform.OS !== 'web') {
      await nativeFirestore().collection('rides').doc(rideId).update({
        'routeDeviation.status': status,
        'routeDeviation.respondedAt': Date.now(),
      });
      return;
    }
    await updateDoc(doc(db, 'rides', rideId), {
      'routeDeviation.status': status,
      'routeDeviation.respondedAt': Date.now(),
    });
  },

  /**
   * ESCUCHA EL VIAJE. De este listener cuelga TODO lo que las dos apps saben
   * del viaje: que el chofer llegó, que arrancó, que terminó, que lo cancelaron.
   *
   * ⚠️ ARREGLO 2026-09 — NI TENÍA RELOJ NI REINTENTO, Y ESO DEJABA LAS DOS
   * PANTALLAS CONGELADAS SIN QUE NADIE SE ENTERARA.
   *
   * Antes esto traducía CUALQUIER error a `callback(null)`. Y los cuatro
   * consumidores (todos en `(tabs)/map.tsx`) empiezan con `if (!data) return;`,
   * o sea que un fallo se veía exactamente igual que "todavía no hay dato":
   * como nada. Peor: **un `onSnapshot` rechazado está muerto — no se reintenta
   * solo** y nadie lo volvía a abrir. Un bache de red de dos segundos, o un
   * rechazo momentáneo mientras se refresca el token, y las dos apps se
   * quedaban mirando una pantalla que ya no iba a cambiar en todo el viaje.
   *
   * Ahora se distinguen las dos cosas, que no son la misma:
   *   · NO HAY DATO  → el documento no existe. Eso sí es `callback(null)`, y es
   *     información verdadera: el viaje no está.
   *   · ESTO SE ROMPIÓ → el listener se cayó. NO se miente con un `null`: se
   *     vuelve a abrir la suscripción, con espera creciente.
   *
   * RELOJ Y SALIDA (regla de la casa: toda espera tiene las dos). Los
   * reintentos son 1 s, 2 s, 4 s, 8 s, 15 s y 15 s — 45 s de caída seguida. Ese
   * número sale de lo que se quiere cubrir: un túnel, un cambio de celda, un
   * token que se renueva. Pasados los seis se PARA y se avisa por
   * `alPerderElViaje`, en vez de reintentar para siempre gastando batería y
   * datos contra algo que no va a volver. Un reintento que funciona pone el
   * contador a cero: la ventana acota el corte, no la vida del viaje.
   *
   * `alPerderElViaje` es opcional y va al final a propósito: las cuatro
   * llamadas que existen hoy siguen valiendo tal cual, sin tocarlas. Quien
   * quiera enseñar "se perdió la conexión con el viaje" ya tiene por dónde —
   * hoy NADIE lo pasa, así que el corte definitivo se queda en el log y la
   * pantalla no lo dice. Se deja dicho, no rellenado.
   *
   * Devuelve, como siempre, la función para darse de baja; ahora también corta
   * el reintento pendiente.
   */
  listenToRideStatus(
    rideId: string,
    callback: (ride: any | null) => void,
    alPerderElViaje?: (motivo: string) => void
  ) {
    /** Esperas entre reintentos. La longitud del array ES el límite. */
    const ESPERAS_MS = [1000, 2000, 4000, 8000, 15000, 15000];

    let vivo = true;
    let bajaDeLaSuscripcion: (() => void) | null = null;
    let temporizador: any = null;
    let intento = 0;

    const abrir = () => {
      if (!vivo) return;

      const alDato = (docSnap: any) => {
        if (!vivo) return;
        // Llegó algo: el listener está sano otra vez y la ventana se reinicia.
        intento = 0;
        if (docSnap.exists()) callback({ id: docSnap.id, ...docSnap.data() });
        else callback(null);
      };

      const alFallo = (err: any) => {
        if (!vivo) return;
        // El listener caído ya no sirve; hay que cerrarlo antes de abrir otro
        // o se acumulan suscripciones muertas.
        try { bajaDeLaSuscripcion?.(); } catch {}
        bajaDeLaSuscripcion = null;

        const espera = ESPERAS_MS[intento];
        if (espera === undefined) {
          vivo = false;
          console.warn(
            '[listenToRideStatus] se agotaron los reintentos, el viaje deja de escucharse:',
            err?.message
          );
          try { alPerderElViaje?.(String(err?.message ?? 'sin detalle')); } catch {}
          return;
        }
        intento += 1;
        console.warn(
          `[listenToRideStatus] caído (${err?.message}); reintento ${intento} de ${ESPERAS_MS.length} en ${espera} ms`
        );
        temporizador = setTimeout(() => { temporizador = null; abrir(); }, espera);
      };

      try {
        bajaDeLaSuscripcion =
          Platform.OS !== 'web'
            ? nativeFirestore().collection('rides').doc(rideId).onSnapshot(alDato, alFallo)
            : onSnapshot(doc(db, 'rides', rideId), alDato, alFallo);
      } catch (e: any) {
        // Abrir la suscripción también puede tirar (SDK sin sesión todavía).
        // Es el mismo fallo y merece el mismo reintento, no un silencio.
        alFallo(e);
      }
    };

    abrir();

    return () => {
      vivo = false;
      if (temporizador) { clearTimeout(temporizador); temporizador = null; }
      try { bajaDeLaSuscripcion?.(); } catch {}
      bajaDeLaSuscripcion = null;
    };
  },

  // --- CHAT IN-RIDE (pasajero ↔ chofer) ---
  async sendRideMessage(rideId: string, senderId: string, text: string) {
    // FIX — Faltaba la rama NATIVA: en el APK el SDK web va anónimo y las
    // reglas rechazan la escritura, así que el mensaje del pasajero NUNCA se
    // guardaba. Crítico: es el canal por el que alguien puede pedir ayuda
    // durante un viaje. nativeFirestore() hereda la sesión real.
    if (Platform.OS !== 'web') {
      return await nativeFirestore()
        .collection('rides').doc(rideId)
        .collection('messages')
        .add({ senderId, text, timestamp: nativeFirestore.FieldValue.serverTimestamp() });
    }
    const messagesRef = collection(db, `rides/${rideId}/messages`);
    return await addDoc(messagesRef, {
      senderId,
      text,
      timestamp: serverTimestamp(),
    });
  },

  /**
   * Mensajes del viaje, en vivo.
   *
   * ⚠️ ESTE LISTENER NO BORRA LA CONVERSACIÓN CUANDO FALLA.
   *
   * Antes su manejador de error hacía `callback([])`. Con la persistencia local
   * encendida (firebaseConfig), el snapshot llega PRIMERO desde la caché — el
   * usuario ve sus mensajes — y si después el servidor responde con un error,
   * ese `callback([])` los BORRABA de la pantalla. Es exactamente lo que
   * capturó el fundador: dos mensajes con su hora, y a los segundos "No hay
   * mensajes aún".
   *
   * En un chat de viaje eso es grave: alguien puede haber escrito pidiendo
   * ayuda y ver cómo su mensaje desaparece.
   *
   * Ahora: si ya se entregó algo bueno, un error posterior NO lo pisa; solo se
   * vacía la primera vez, para que la pantalla pueda salir del estado de carga.
   * El error se avisa aparte por `onError`, para poder distinguir "no hay
   * mensajes" de "no pudimos cargarlos".
   */
  listenToRideMessages(
    rideId: string,
    callback: (messages: any[]) => void,
    onError?: (mensaje: string) => void
  ) {
    let yaEntregoAlgo = false;
    const alFallar = (err: any) => {
      console.warn('[listenToRideMessages]', err?.code, err?.message);
      onError?.(String(err?.code || err?.message || 'error'));
      if (!yaEntregoAlgo) callback([]);
    };
    const alLlegar = (docs: any[]) => {
      yaEntregoAlgo = true;
      callback(docs);
    };

    if (Platform.OS !== 'web') {
      return nativeFirestore()
        .collection('rides')
        .doc(rideId)
        .collection('messages')
        .orderBy('timestamp', 'asc')
        .onSnapshot(
          (snap: any) => alLlegar(snap.docs.map((d: any) => ({ id: d.id, ...d.data() }))),
          alFallar
        );
    }
    const messagesRef = collection(db, `rides/${rideId}/messages`);
    const q = query(messagesRef, orderBy('timestamp', 'asc'));
    return onSnapshot(
      q,
      (snapshot) => alLlegar(snapshot.docs.map(d => ({ id: d.id, ...d.data() }))),
      alFallar
    );
  },

  /**
   * @param motivoDeEspera Solo para la cancelación del chofer que esperó sus
   * minutos y el pasajero no apareció. Es un CÓDIGO de una lista cerrada
   * (`MOTIVOS_DE_ESPERA` en components/MotivoDeEsperaModal.tsx), no texto
   * libre, y va en su propio campo a propósito: `cancelReason` es texto que
   * escribe el cliente y el servidor NO lo usa para decidir nada desde que una
   * exención basada en él resultó ser un agujero (ver el comentario de
   * `onRideCancelledPenalty`, functions/src/penalties.ts).
   *
   * Y este campo tampoco decide: la exención la decide el servidor con el sello
   * de llegada y su propio reloj. El motivo es para el registro y para poder
   * decirle al pasajero qué pasó.
   */
  async cancelRide(
    rideId: string,
    reason?: string,
    cancelledBy?: 'PASSENGER' | 'DRIVER',
    motivoDeEspera?: string
  ) {
    // FIX AUDITORÍA (framing de multa) — Escribimos `cancelledBy` EXPLÍCITO. Las
    // reglas Firestore impiden que un cliente lo atribuya a la contraparte
    // (un pasajero no puede poner 'DRIVER' ni viceversa), así que la Cloud
    // Function de multas confía en este campo para saber QUIÉN canceló. Antes
    // se derivaba de `cancelReason` (texto libre, falsificable) → un pasajero
    // podía endosar la cancelación a un chofer honesto.
    const patch: any = { status: 'CANCELLED', cancelReason: reason ?? null };
    if (cancelledBy) patch.cancelledBy = cancelledBy;
    if (motivoDeEspera) patch.motivoDeEspera = motivoDeEspera;

    // ── H05 / H07 / H23 (auditoría 16-sep-2026): POR QUÉ ES UNA TRANSACCIÓN ──
    //
    // Antes era un `update()` a secas, y las dos pantallas de "Cancelar Viaje"
    // (map.tsx) anunciaban el éxito aunque el servidor lo rechazara. Arreglar
    // solo la pantalla NO bastaba, y esta es la razón:
    //
    // Firestore aplica un `update()` en la caché del teléfono ANTES de que el
    // servidor conteste, y los listeners disparan con ese dato provisional. El
    // listener del viaje veía CANCELLED al instante y limpiaba todo —pantalla,
    // sesión en disco y los propios listeners— antes de saber si la regla lo
    // iba a aceptar. Si la regla lo denegaba, Firestore deshacía el cambio en
    // la caché… pero ya no quedaba nadie escuchando: el viaje seguía vivo en el
    // servidor, con el chofer manejando hacia alguien que "ya había cancelado".
    //
    // Una transacción NO escribe nada en la caché hasta que el servidor la
    // confirma. Así el listener solo ve CANCELLED cuando es verdad, y un
    // rechazo llega a la pantalla con el viaje todavía montado. Sin conexión
    // falla en vez de quedarse encolada, que es justo lo honesto aquí: la
    // persona se entera de que NO canceló.
    //
    // De paso lee el estado real: si el viaje ya terminó no escribe nada.
    // Reescribir un CANCELLED lo permiten las reglas (`cur == next`) y pisaba
    // `cancelledBy`: el pasajero que tocaba "Cancelar" un segundo después que
    // el chofer se quedaba registrado como el que canceló.
    //
    // El `patch` es EXACTAMENTE el mismo de antes (estado, motivo, firma y
    // fecha): reglas, multa y panel de admin reciben lo que ya recibían.
    const CANCELABLES = ['PENDING', 'ACCEPTED', 'ARRIVED', 'IN_PROGRESS'];
    let estadoEncontrado: string | null;
    if (Platform.OS !== 'web') {
      const ref = nativeFirestore().collection('rides').doc(rideId);
      estadoEncontrado = await nativeFirestore().runTransaction(async (tx: any) => {
        const snap = await tx.get(ref);
        if (!snap.exists()) return null;
        const estado = String(snap.data()?.status ?? '');
        // Sin `throw` para el caso "ya terminó": no hay nada que descartar,
        // simplemente no se escribe. El aviso se lanza FUERA de la transacción.
        if (CANCELABLES.includes(estado)) {
          tx.update(ref, { ...patch, cancelledAt: nativeFirestore.FieldValue.serverTimestamp() });
        }
        return estado;
      });
    } else {
      const ref = doc(db, 'rides', rideId);
      estadoEncontrado = await runTransaction(db, async (tx) => {
        const snap = await tx.get(ref);
        if (!snap.exists()) return null;
        const estado = String((snap.data() as any)?.status ?? '');
        if (CANCELABLES.includes(estado)) {
          tx.update(ref, { ...patch, cancelledAt: serverTimestamp() });
        }
        return estado;
      });
    }
    if (estadoEncontrado === null || !CANCELABLES.includes(estadoEncontrado)) {
      // CONTRATO CON map.tsx — NO RENOMBRAR `code` ni `estado`. La pantalla
      // distingue "ya estaba terminado" (el viaje no existe o está en
      // COMPLETED/CANCELLED/EXPIRED; `estado` null = no existe) de un fallo de
      // red o de reglas, que sí deja el viaje montado.
      const err: any = new Error('Este viaje ya no está activo.');
      err.code = 'ride/ya-terminado';
      err.estado = estadoEncontrado;
      throw err;
    }
  },

  async completeRide(rideId: string) {
    if (Platform.OS !== 'web') {
      await nativeFirestore().collection('rides').doc(rideId).update({
        status: 'COMPLETED',
        completedAt: nativeFirestore.FieldValue.serverTimestamp(),
      });
      return;
    }
    const rideRef = doc(db, 'rides', rideId);
    await updateDoc(rideRef, {
      status: 'COMPLETED',
      completedAt: serverTimestamp()
    });
  },

  /**
   * MODELO UBER — El chofer llegó al punto de recogida pero el pasajero aún
   * NO se ha subido. Uber usa este estado para (a) mostrar al pasajero
   * "Tu chofer llegó", (b) empezar a cobrar wait-time fees después de
   * cierto tiempo, y (c) permitir que el chofer inicie el viaje solo
   * cuando el pasajero esté físicamente en el vehículo. Antes el chofer
   * pasaba directo de ACCEPTED a IN_PROGRESS al aceptar → mala UX y
   * fraude fácil.
   *
   * Ref: https://help.uber.com/business/article/ride-statuses-and-what-they-mean
   */
  async driverArrivedAtPickup(rideId: string) {
    if (Platform.OS !== 'web') {
      await nativeFirestore().collection('rides').doc(rideId).update({
        status: 'ARRIVED',
        arrivedAt: nativeFirestore.FieldValue.serverTimestamp(),
      });
      return;
    }
    await updateDoc(doc(db, 'rides', rideId), {
      status: 'ARRIVED',
      arrivedAt: serverTimestamp(),
    });
  },

  // ─────────────────────────────────────────────────────────────────────────
  // AQUÍ VIVÍA `startRide(rideId, enteredPin?)`. BORRADO A PROPÓSITO — no lo
  // vuelvas a poner.
  //
  // Era una trampa cargada: comparaba el PIN tecleado contra `ride.ridePin`,
  // un campo que ya NO existe (el PIN se mudó a /rides/{id}/private/security,
  // invisible al chofer por regla). Como `expected` salía siempre undefined,
  // el `if` de validación nunca se cumplía y la función arrancaba el viaje
  // CON CUALQUIER PIN — o sin ninguno. Nadie la llamaba hoy, pero el primer
  // desarrollador que buscara "startRide" la habría usado creyendo que valida.
  //
  // El único camino válido para arrancar un viaje es la Cloud Function
  // `startRideWithPin` (functions/src/rides.ts), que compara server-side, lleva
  // tope de 8 intentos, y es la única autorizada a escribir IN_PROGRESS
  // (firestore.rules prohíbe ese estado a TODO cliente). Se llama desde
  // src/services/cloudFunctions.ts → callStartRideWithPin.
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * BOOTSTRAP RESUME — Consulta puntual (sin listener) del ride ACTIVO del
   * usuario al arrancar la app. Uber usa este mismo patrón: cuando la app
   * abre en cold-start después de un force-close, consulta el backend por
   * "¿tengo algún ride abierto?" antes de decidir qué pantalla mostrar.
   *
   * IMPORTANTE — Antes usábamos `where('status','in',[...])` combinado con
   * `where(passengerId==uid)` que dispara FAILED_PRECONDITION (requiere
   * índice compuesto) en cold projects. Ahora consultamos solo por
   * passengerId/driverId y filtramos el status en memoria — un ride tiene
   * pocos estados posibles y la query devuelve <10 docs en el 99% de casos.
   *
   * Devuelve el ride si existe (con id) o null. NUNCA lanza.
   */
  async findActiveRideForUser(
    uid: string,
    role: 'PASSENGER' | 'DRIVER'
  ): Promise<any | null> {
    const activeStatuses = new Set(
      role === 'PASSENGER'
        ? ['PENDING', 'ACCEPTED', 'ARRIVED', 'IN_PROGRESS']
        : ['ACCEPTED', 'ARRIVED', 'IN_PROGRESS']
    );
    const field = role === 'PASSENGER' ? 'passengerId' : 'driverId';
    // Filtra por status activo Y frescura. Los stale (>4h) se auto-expiran
    // para que no vuelvan a aparecer en el próximo arranque.
    const filterActiveFresh = (docs: any[]): any[] => {
      const out: any[] = [];
      for (const r of docs) {
        if (!activeStatuses.has(String(r.status ?? ''))) continue;
        if (isRideFresh(r, false)) {
          out.push(r);
        } else {
          // Ride viejo abandonado → limpiar del backend (anti fantasma).
          autoExpireStaleRide(r.id);
        }
      }
      return out;
    };
    const pickMostRecent = (docs: any[]): any | null => {
      if (docs.length === 0) return null;
      docs.sort((a, b) => rideLastActivityMs(b) - rideLastActivityMs(a));
      return docs[0];
    };
    // ── BLOQUE K — EL VIAJE ACTIVO SE PERDÍA A PARTIR DEL VIAJE 21 ────────
    // `limit(20)` SIN `orderBy` no significa "los 20 últimos": Firestore
    // ordena por id de documento, que es aleatorio. Un usuario con 21 viajes
    // o más recibía 20 documentos cualesquiera de toda su historia, y el viaje
    // que tenía EN CURSO podía sencillamente no venir en el paquete: reabría
    // la app y su viaje había desaparecido. La rama web era peor todavía —sin
    // límite ninguno— y leía (y facturaba) el historial completo en cada
    // arranque.
    //
    // Ahora las dos ramas piden lo mismo: los 20 más RECIENTES por fecha.
    // Los índices compuestos que esto necesita (passengerId+createdAt DESC y
    // driverId+createdAt DESC) ya están declarados en firestore.indexes.json.
    //
    // ⚠️ `orderBy('createdAt')` descarta los documentos que NO tengan ese
    // campo. Todo viaje lo lleva desde su creación (requestRide lo escribe
    // siempre), pero si el índice no estuviera desplegado la consulta fallaría
    // entera y el usuario se quedaría igual de perdido que antes — por eso el
    // catch reintenta a la antigua en vez de devolver null.
    const ordenados = async (): Promise<any[]> => {
      if (Platform.OS !== 'web') {
        const snap = await nativeFirestore()
          .collection('rides')
          .where(field, '==', uid)
          .orderBy('createdAt', 'desc')
          .limit(20)
          .get();
        return snap.docs.map((d: any) => ({ id: d.id, ...d.data() }));
      }
      const q = query(
        collection(db, 'rides'),
        where(field, '==', uid),
        orderBy('createdAt', 'desc'),
        limit(20)
      );
      const snap = await getDocs(q);
      return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    };
    const sinOrden = async (): Promise<any[]> => {
      if (Platform.OS !== 'web') {
        const snap = await nativeFirestore()
          .collection('rides')
          .where(field, '==', uid)
          .limit(20)
          .get();
        return snap.docs.map((d: any) => ({ id: d.id, ...d.data() }));
      }
      const q = query(collection(db, 'rides'), where(field, '==', uid), limit(20));
      const snap = await getDocs(q);
      return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    };
    try {
      let docs: any[];
      try {
        docs = await ordenados();
      } catch (eOrden: any) {
        // Falta el índice compuesto (FAILED_PRECONDITION) o cualquier otro
        // fallo del orden: mejor la consulta vieja que ningún viaje.
        console.warn('[findActiveRideForUser] consulta ordenada falló, reintento sin orden:', eOrden?.message);
        docs = await sinOrden();
      }
      const active = filterActiveFresh(docs);
      return pickMostRecent(active);
    } catch (e: any) {
      console.warn('[findActiveRideForUser] falló:', e?.message);
      return null;
    }
  },

  /**
   * Cancela un ride HUÉRFANO — uno con mi driverId + status activo que el
   * device NO reconoce como propio (ni oferta pendiente ni viaje activo).
   * El caller (map.tsx gate) solo lo invoca para rides con >10 min de edad,
   * evitando matar un viaje legítimo recién aceptado en otro dispositivo.
   * Idempotente vía _autoExpiredRideIds.
   */
  async forceExpireOrphanRide(rideId: string): Promise<void> {
    autoExpireStaleRide(rideId); // reutiliza el guard de duplicados + write
  },

  /**
   * FIX BUG #3 (causa raíz #2) — ¿Este ride está DEMASIADO viejo para
   * restaurar (abandonado >4h)? El resume por ID (getRideById) NO aplicaba el
   * chequeo de frescura que sí usa findActiveRideForUser, así que un ride viejo
   * colgado en ACCEPTED/ARRIVED se re-montaba en cada arranque → si montarlo
   * fallaba, entraba en crash-loop hasta que el server lo expiraba (~4h). Ahora
   * el resume lo detecta y lo auto-expira antes de intentar montarlo.
   */
  isRideStale(ride: any): boolean {
    return !isRideFresh(ride, false);
  },

  /**
   * Lectura puntual de un ride por su ID exacto. Usado en deep-link
   * (tap de notificación con rideId) para restaurar el ride específico
   * sin depender de queries compuestas.
   */
  async getRideById(rideId: string): Promise<any | null> {
    // FIX BUG #4/#3 (causa raíz #2) — Distingue "NO existe" (return null) de
    // "la lectura FALLÓ" (throw). Antes el try/catch devolvía null en AMBOS
    // casos, así que un error transitorio en el arranque (sesión nativa aún sin
    // token, permission-denied momentáneo) se confundía con "el viaje no
    // existe" y el bootstrap BORRABA el puntero del viaje de forma PERMANENTE →
    // al volver de background pedía buscar el viaje otra vez. Ahora el error se
    // propaga y el caller decide (conservar el puntero y reintentar).
    if (Platform.OS !== 'web') {
      const snap: any = await nativeFirestore().collection('rides').doc(rideId).get();
      if (!snap.exists()) return null;
      return { id: snap.id, ...snap.data() };
    }
    const snap = await getDoc(doc(db, 'rides', rideId));
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() };
  },

  /**
   * Suscripción del pasajero al SU ride activo (paralelo al del chofer).
   * Cuando la app hace resume desde una notificación o cold-start,
   * necesitamos escuchar los cambios de estado del ride sin conocer su ID
   * de antemano.
   */
  listenToMyActiveRideAsPassenger(
    passengerId: string,
    callback: (ride: any | null) => void
  ) {
    // Mismo patrón que findActiveRideForUser — evitar composite index
    // filtrando el status en memoria + descartar rides fantasma (>4h).
    const ACTIVE = new Set(['PENDING', 'ACCEPTED', 'ARRIVED', 'IN_PROGRESS']);
    const pickMostRecent = (docs: any[]) => {
      const active: any[] = [];
      for (const r of docs) {
        if (!ACTIVE.has(String(r.status ?? ''))) continue;
        // En snapshot en vivo asumimos fresco si no hay timestamp (doc recién
        // creado con serverTimestamp aún sin resolver), pero expiramos los
        // que SÍ tienen fecha vieja.
        if (isRideFresh(r, true)) active.push(r);
        else autoExpireStaleRide(r.id);
      }
      active.sort((a: any, b: any) => rideLastActivityMs(b) - rideLastActivityMs(a));
      return active[0] ?? null;
    };
    if (Platform.OS !== 'web') {
      return nativeFirestore()
        .collection('rides')
        .where('passengerId', '==', passengerId)
        .onSnapshot(
          (snap: any) => {
            const rides = snap.docs.map((d: any) => ({ id: d.id, ...d.data() }));
            callback(pickMostRecent(rides));
          },
          (err: any) => { console.warn('[listenToMyActiveRideAsPassenger]', err?.message); callback(null); }
        );
    }
    const q = query(collection(db, 'rides'), where('passengerId', '==', passengerId));
    return onSnapshot(
      q,
      (snap) => {
        const rides = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        callback(pickMostRecent(rides));
      },
      (err) => {
        console.warn('[listenToMyActiveRideAsPassenger] web error:', err?.message);
        callback(null);
      }
    );
  },

  /**
   * Suscripción del chofer a los rides ACEPTADOS por un pasajero PARA él.
   * Necesario porque el chofer envía una oferta y luego debe saber si el
   * pasajero lo acepta (y no otro chofer). El listener del ride específico
   * ya lo cubre — este método es la versión "listener sin conocer el ride
   * id de antemano" que consulta todas las ofertas ACEPTADAS del driver.
   */
  listenToMyAcceptedRideAsDriver(
    driverId: string,
    callback: (ride: any | null) => void,
    // V13 — el viaje que la pantalla ya lleva montado. Si un pasajero acepta
    // tarde una oferta vieja, el "más reciente" sería ESE otro y la pantalla
    // perdería los cambios del viaje real. Opcional: sin él, todo sigue igual.
    preferirRideId?: () => string | null
  ) {
    // H1 FIX — Elegir el ride MÁS RECIENTE si por alguna condición de carrera
    // hay más de uno con driverId=me + status activo.
    // FIX VIAJE FANTASMA — Descartar rides ABANDONADOS (>4h sin cerrar). El
    // bug reportado: al conectarse el chofer, un ride viejo ACCEPTED de una
    // prueba anterior resucitaba como "Viaje en Curso" con un pasajero que no
    // estaba solicitando nada. Ahora esos rides se filtran Y se auto-expiran.
    const ACTIVE = new Set(['ACCEPTED', 'ARRIVED', 'IN_PROGRESS']);
    const pickMostRecent = (docs: any[]) => {
      const active: any[] = [];
      for (const r of docs) {
        if (!ACTIVE.has(String(r.status ?? ''))) continue;
        // assumeFreshIfNoTs=true: un ride recién aceptado puede no tener
        // acceptedAt resuelto todavía en el primer snapshot. Pero si tiene
        // timestamp y es viejo, lo expiramos.
        if (isRideFresh(r, true)) active.push(r);
        else autoExpireStaleRide(r.id);
      }
      const preferido = preferirRideId?.() ?? null;
      if (preferido) {
        const elMismo = active.find((r: any) => String(r.id) === String(preferido));
        if (elMismo) return elMismo;
      }
      active.sort((a: any, b: any) => rideLastActivityMs(b) - rideLastActivityMs(a));
      return active[0] ?? null;
    };
    if (Platform.OS !== 'web') {
      return nativeFirestore()
        .collection('rides')
        .where('driverId', '==', driverId)
        // TOPE — antes leía el historial COMPLETO del chofer cada vez que
        // tocaba "Conectarse": el chofer más fiel era el que más pagaba.
        // El índice compuesto ya existe (firestore.indexes.json).
        .orderBy('createdAt', 'desc')
        .limit(10)
        .onSnapshot(
          (snap: any) => {
            const rides = snap.docs.map((d: any) => ({ id: d.id, ...d.data() }));
            callback(pickMostRecent(rides));
          },
          (err: any) => { console.warn('[listenToMyAcceptedRideAsDriver]', err?.message); callback(null); }
        );
    }
    const q = query(
      collection(db, 'rides'),
      where('driverId', '==', driverId),
      orderBy('createdAt', 'desc'),
      limit(10)
    );
    return onSnapshot(
      q,
      (snap) => {
        const rides = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        callback(pickMostRecent(rides));
      },
      (err) => {
        console.warn('[listenToMyAcceptedRideAsDriver] web error:', err?.message);
        callback(null);
      }
    );
  },

  // ══ AQUI VIVIA `submitDriverRating`, Y SE BORRO A PROPOSITO ══════════
  //
  // Escribia `passengerRating` DIRECTO en el documento del viaje desde el
  // telefono. No tenia ni un llamador (comprobado con grep en todo src/ antes
  // de borrarla), y desde esta ronda ese campo lo escribe solo el servidor
  // (`submitPassengerRating`, functions/src/ratings.ts) y las reglas lo
  // bloquean al cliente.
  //
  // Se borra en vez de dejarla porque un metodo asi es un arma cargada: el dia
  // que alguien lo llame, la regla denegara y el fallo aparecera como un
  // 'permission-denied' generico en una pantalla que no tiene nada que ver.
  // Para calificar al pasajero se usa `callSubmitPassengerRating`
  // (services/cloudFunctions.ts), que ademas comprueba en el servidor que quien
  // califica sea el chofer de ESE viaje.

  // --- VEHICULOS (FLOTA / MARKETPLACE) ---
  /**
   * EL ÚNICO SITIO POR EL QUE SE CREA UN VEHÍCULO DE FLOTA.
   *
   * ⚠️ Pasa por `paraGuardar` y no es cosmética: había dos formularios
   * escribiendo NOMBRES DISTINTOS para la misma cosa (`make` vs `brand`, con y
   * sin `year`, con y sin `plate`), y cada pantalla leía los del otro. El carro
   * dado de alta por el panel salía en la vitrina sin nombre y sin año; el dado
   * de alta por Flota salía en la tabla del panel sin marca y sin placa.
   * La historia completa está en `constants/vehiculoDeFlota.ts`.
   *
   * Normalizar AQUÍ —y no en cada formulario— es lo que hace que no pueda
   * volver a pasar: cualquiera que llame a esto acaba con la misma forma.
   */
  async addVehicle(ownerId: string, vehicleData: any) {
    // SDK nativo en móvil: con el web la regla denegaba y el dueño de Rent Car
    // tocaba "Guardar vehículo" sin que se creara nada.
    return await agregarDoc(['vehicles'], {
      ownerId,
      ...paraGuardar(vehicleData),
      createdAt: new Date(),
      isPublished: false
    });
  },

  /**
   * LOS VEHICULOS DEL CHOFER — solo lectura.
   *
   * `users/{uid}/vehiculos` esta en `allow write: if false` (firestore.rules):
   * la lista la escriben unicamente las Cloud Functions de vehiculos.ts. Aqui
   * solo se lee, y por eso no hay ni un `set` en todo este metodo.
   *
   * SDK nativo en movil y web en el navegador, igual que el resto del archivo:
   * con el SDK web dentro del APK las lecturas salen sin `request.auth` y la
   * regla `isSelf(uid)` las deniega.
   */
  async listarMisVehiculos(uid: string): Promise<any[]> {
    if (!uid) return [];
    if (Platform.OS !== 'web') {
      const snap = await nativeFirestore()
        .collection('users')
        .doc(uid)
        .collection('vehiculos')
        .get();
      return snap.docs.map((d: any) => ({ id: d.id, ...d.data() }));
    }
    const snap = await getDocs(collection(db, 'users', uid, 'vehiculos'));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  },

  async getMarketplaceVehicles() {
    const q = query(collection(db, 'vehicles'), where("isPublished", "==", true));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },

  listenToMarketplaceVehicles(callback: (vehicles: any[]) => void) {
    // FIX MONETIZACIÓN — Filtramos los carros con cupo pendiente de pago
    // (4º en adelante sin pagar) para que NO aparezcan en el marketplace,
    // aunque el dueño los haya marcado isPublished. requiresSlotPayment lo
    // pone la CF y solo el backend lo limpia tras el cobro.
    const notPendingSlot = (v: any) => v?.requiresSlotPayment !== true;
    if (Platform.OS !== 'web') {
      return nativeFirestore()
        .collection('vehicles')
        .where('isPublished', '==', true)
        .onSnapshot(
          (snap: any) => {
            callback(snap.docs.map((d: any) => ({ id: d.id, ...d.data() })).filter(notPendingSlot));
          },
          (err: any) => {
            // CRÍTICO — este handler SOLO hacía console.warn, sin llamar al
            // callback. Como la pantalla apaga su spinner dentro del callback,
            // cualquier error dejaba el marketplace GIRANDO PARA SIEMPRE.
            // Le pasaba a todo invitado (leer /vehicles exige sesión) y a
            // cualquiera con un fallo de red. Es "Your app does not open or
            // load" al pie de la letra — el rechazo vc16 de Google Play.
            // La rama web de abajo sí lo hacía bien; faltaba aquí.
            console.warn('[listenToMarketplaceVehicles] nativo:', err?.message);
            callback([]);
          }
        );
    }
    const q = query(collection(db, 'vehicles'), where("isPublished", "==", true));
    return onSnapshot(
      q,
      (snapshot) => {
        const vehicles = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).filter(notPendingSlot);
        callback(vehicles);
      },
      (err) => {
        console.warn('[listenToMarketplaceVehicles] web error:', err?.message);
        callback([]);
      }
    );
  },

  async getOwnerVehicles(ownerId: string) {
    const q = query(collection(db, 'vehicles'), where("ownerId", "==", ownerId));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },

  /**
   * TODAS MIS CONVERSACIONES DE RENT CAR, SEA YO EL DUEÑO O EL CHOFER.
   *
   * ── POR QUÉ HACE FALTA ─────────────────────────────────────────────────
   *
   * La que había, `listenToOwnerNegotiations`, busca por el PREFIJO del id del
   * documento (`${ownerId}__${renterId}`), así que solo encuentra las
   * conversaciones de quien está en el PRIMER hueco: el dueño.
   *
   * Y la pestaña «Chats» que la usa estaba colgada con
   * `href: isRenter ? ... : null` — o sea, se le enseñaba al CHOFER. Un chofer
   * pasando su uid por el prefijo no encuentra nunca nada, porque su uid va en
   * el segundo hueco. Resultado: la pestaña del chofer salía siempre vacía y el
   * dueño no tenía pestaña. Los mensajes llegaban a Firestore y no los leía
   * nadie.
   *
   * Esto busca por `participants`, que el propio `sendRentCarMessage` ya
   * escribe en el documento padre con los dos uids. Es además el campo por el
   * que las reglas autorizan la lectura (`request.auth.uid in
   * resource.data.participants`), así que la consulta y el permiso miran lo
   * mismo.
   *
   * Sin `orderBy`: `array-contains` + `orderBy` exige un índice compuesto, y
   * una consulta que falla por falta de índice no da error en pantalla, se
   * queda muda. Son pocas conversaciones: se ordenan aquí.
   */
  listenToMisNegociaciones(uid: string, callback: (items: any[]) => void) {
    const ordenar = (items: any[]) =>
      items.sort(
        (a, b) => (b.updatedAt?.toMillis?.() ?? 0) - (a.updatedAt?.toMillis?.() ?? 0)
      );

    if (Platform.OS !== 'web') {
      return nativeFirestore()
        .collection('rent_negotiations')
        .where('participants', 'array-contains', uid)
        .onSnapshot(
          (snap: any) => callback(ordenar(snap.docs.map((d: any) => ({ id: d.id, ...d.data() })))),
          (err: any) => {
            console.warn('[listenToMisNegociaciones]', err?.message);
            callback([]);
          }
        );
    }
    return onSnapshot(
      query(collection(db, 'rent_negotiations'), where('participants', 'array-contains', uid)),
      (snap) => callback(ordenar(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })))),
      (err) => {
        console.warn('[listenToMisNegociaciones] web:', err?.message);
        callback([]);
      }
    );
  },

  // --- CRM RENT A CAR ---
  async sendRentCarMessage(chatId: string, messageData: any) {
    // AUDIT FIX RENT — Ahora usa SDK nativo en móvil para heredar la sesión
    // del custom token (mismo issue documentado en sendRideOffer). Sin esto
    // los mensajes iban anónimos y las security rules los rechazaban en
    // silencio → el chat parecía roto entre owner y candidato.
    //
    // FIX AUDIT — Además UPSERT del doc padre `rent_negotiations/{chatId}` con
    // `participants: [ownerId, renterId]`. Antes el padre no existía y la
    // bandeja del dueño (listenToOwnerNegotiations) nunca lo encontraba →
    // negociaciones invisibles. El chatId es `${ownerId}__${renterId}`.
    const [ownerId, renterId] = String(chatId).split('__');
    const participants = [ownerId, renterId].filter(Boolean);
    const parentData: any = {
      participants,
      ownerId: ownerId || null,
      renterId: renterId || null,
      lastMessage: String(messageData.text ?? '').slice(0, 200),
      partnerName: messageData.partnerName ?? null,
      partnerVehicle: messageData.partnerVehicle ?? null,
    };
    if (Platform.OS !== 'web') {
      const ref = nativeFirestore().collection('rent_negotiations').doc(chatId);
      await ref.set({ ...parentData, updatedAt: nativeFirestore.FieldValue.serverTimestamp() }, { merge: true });
      return await ref
        .collection('messages')
        .add({ ...messageData, timestamp: nativeFirestore.FieldValue.serverTimestamp() });
    }
    await setDoc(doc(db, 'rent_negotiations', chatId), { ...parentData, updatedAt: serverTimestamp() }, { merge: true });
    const messagesRef = collection(db, `rent_negotiations/${chatId}/messages`);
    return await addDoc(messagesRef, { ...messageData, timestamp: serverTimestamp() });
  },

  /**
   * AUDIT FIX RENT — Nuevo listener para los mensajes de una negociación
   * específica. Antes rent-car-chat.tsx solo tenía state local — nada
   * se sincronizaba entre owner y candidato. Ahora ambos ven el chat en
   * vivo con el mismo chatId determinístico.
   */
  listenToRentNegotiationMessages(chatId: string, uid: string, callback: (msgs: any[]) => void) {
    // V07 — Una consulta sin filtro sobre /messages es un `list` que la regla
    // (senderId == uid || recipientId == uid) no puede demostrar: salía
    // permission-denied y el chat quedaba vacío para las dos partes. Se abren
    // DOS consultas, cada una cumple por sí sola una rama de la regla, y se
    // fusionan en memoria. Sin orderBy a propósito: con un where de un solo
    // campo no hace falta índice compuesto; el orden se pone aquí.
    if (!chatId || !uid) return () => {};
    const enviados = new Map<string, any>();
    const recibidos = new Map<string, any>();
    const msDe = (m: any): number => {
      const ms = m?.timestamp?.toMillis?.();
      // serverTimestamp todavía pendiente: es un mensaje de ahora mismo.
      return typeof ms === 'number' && Number.isFinite(ms) ? ms : Date.now();
    };
    const emitir = () => {
      const todos = new Map<string, any>([...enviados, ...recibidos]);
      callback([...todos.values()].sort((a, b) => msDe(a) - msDe(b)));
    };
    const volcar = (destino: Map<string, any>, docs: any[]) => {
      destino.clear();
      for (const d of docs) destino.set(d.id, { id: d.id, ...d.data() });
      emitir();
    };
    // Ante un error NO se borra lo ya cargado: basta con dejar constancia.
    const alFallar = (err: any) => console.warn('[listenToRentNegotiationMessages]', err?.message);

    if (Platform.OS !== 'web') {
      const ref = nativeFirestore().collection('rent_negotiations').doc(chatId).collection('messages');
      const u1 = ref.where('senderId', '==', uid).onSnapshot((snap: any) => volcar(enviados, snap.docs), alFallar);
      const u2 = ref.where('recipientId', '==', uid).onSnapshot((snap: any) => volcar(recibidos, snap.docs), alFallar);
      return () => { try { u1(); } catch { /* ya cerrada */ } try { u2(); } catch { /* ya cerrada */ } };
    }
    const ref = collection(db, `rent_negotiations/${chatId}/messages`);
    const w1 = onSnapshot(query(ref, where('senderId', '==', uid)), (snap) => volcar(enviados, snap.docs), alFallar);
    const w2 = onSnapshot(query(ref, where('recipientId', '==', uid)), (snap) => volcar(recibidos, snap.docs), alFallar);
    return () => { try { w1(); } catch { /* ya cerrada */ } try { w2(); } catch { /* ya cerrada */ } };
  },

  /**
   * AUDIT FIX RENT — Lista todas las negociaciones abiertas de un owner
   * para poblar rent-chats.tsx (que antes tenía `chats: [] = []` hardcoded).
   * Filtra por documentos raíz que empiezan con el ownerUid — nuestro chatId
   * es `${ownerUid}_slug_slug` así que un prefix match resuelve la query
   * sin composite index.
   */
  listenToOwnerNegotiations(ownerUid: string, callback: (chats: any[]) => void) {
    // Firestore no soporta `startsWith` sobre document IDs. Usamos el truco
    // de rango: ownerUid_ ≤ id < ownerUid_￿ (￿ = U+FFFF, mayor que cualquier char).
    const prefix = `${ownerUid}_`;
    const end = `${ownerUid}_`;
    if (Platform.OS !== 'web') {
      return nativeFirestore()
        .collection('rent_negotiations')
        .where('participants', 'array-contains', ownerUid)
        .onSnapshot((snap: any) => {
          callback(snap.docs.map((d: any) => ({ id: d.id, ...d.data() })));
        }, (err: any) => { console.warn('[listenToOwnerNegotiations]', err?.message); callback([]); });
    }
    const q = query(
      collection(db, 'rent_negotiations'),
      where('participants', 'array-contains', ownerUid)
    );
    return onSnapshot(
      q,
      (snap) => {
        callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      },
      (err) => {
        console.warn('[listenToOwnerNegotiations] web error:', err?.message);
        callback([]);
      }
    );
  },

  async approveCandidateToCRM(ownerId: string, candidateData: any) {
    // 2. Transfiere el candidato al CRM del dueño
    return await agregarDoc(['users', ownerId, 'crm_drivers'], {
      ...candidateData,
      status: 'PENDING_DOCS',
      addedAt: selloServidor(),
    });
  },

  listenToOwnerVehicles(ownerId: string, callback: (vehicles: any[]) => void) {
    if (Platform.OS !== 'web') {
      return nativeFirestore()
        .collection('vehicles')
        .where('ownerId', '==', ownerId)
        .onSnapshot((snap: any) => {
          callback(snap.docs.map((d: any) => ({ id: d.id, ...d.data() })));
        }, (err: any) => { console.warn('[listenToOwnerVehicles]', err?.message); callback([]); });
    }
    const q = query(collection(db, 'vehicles'), where("ownerId", "==", ownerId));
    return onSnapshot(
      q,
      (snapshot) => {
        const vehicles = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        callback(vehicles);
      },
      (err) => {
        console.warn('[listenToOwnerVehicles] web error:', err?.message);
        callback([]);
      }
    );
  },

  async toggleVehiclePublishStatus(vehicleId: string, isPublished: boolean) {
    // Alcanzable desde el interruptor de Flota. Con el SDK web en móvil el
    // interruptor se movía en pantalla y el vehículo nunca llegaba al
    // Marketplace: el fallo se lo tragaba un `.catch(console.error)` del store.
    await actualizarDoc(['vehicles', vehicleId], { isPublished });
  },

  // --- ESTUDIANTES Y CHOFERES ESCOLARES ---
  async addStudent(parentId: string, studentData: any) {
    // AUDIT FIX SCHOOL — Antes solo escribíamos a /users/{parentId}/students,
    // que el director NO puede leer (rules lo bloquean como cross-user access).
    // Ahora también creamos un doc espejo en /students root con parentId
    // + schoolId (extraído del nombre del colegio) para que el director
    // pueda listar los estudiantes de su colegio con listenToSchoolStudents.
    const studentsRef = collection(db, `users/${parentId}/students`);
    const now = new Date();
    const rootRef = collection(db, 'students');
    const [subRef, rootDocRef] = await Promise.all([
      addDoc(studentsRef, { ...studentData, parentId, createdAt: now }),
      addDoc(rootRef, {
        ...studentData,
        parentId,
        // schoolId es el nombre normalizado del colegio hasta que tengamos
        // un catálogo real. Suficiente para el director filtrar en cliente.
        schoolId: String(studentData?.school ?? '').trim().toLowerCase().replace(/\s+/g, '-'),
        createdAt: now,
      }),
    ]);
    return { subId: subRef.id, rootId: rootDocRef.id };
  },

  /**
   * AUDIT FIX SCHOOL — El padre necesita ver el estado del estudiante en
   * tiempo real: "Esperando" → "A bordo" → "Entregado". Antes su UI leía
   * del store local (globalStudents) y nunca se sincronizaba con las
   * escrituras que hacía el driver. Ahora escucha su sub-colección directa.
   */
  listenToParentStudents(parentId: string, callback: (students: any[]) => void) {
    if (Platform.OS !== 'web') {
      return nativeFirestore()
        .collection('users')
        .doc(parentId)
        .collection('students')
        .onSnapshot((snap: any) => {
          callback(snap.docs.map((d: any) => ({ id: d.id, ...d.data() })));
        }, (err: any) => { console.warn('[listenToParentStudents]', err?.message); callback([]); });
    }
    const ref = collection(db, 'users', parentId, 'students');
    return onSnapshot(
      ref,
      (snap) => {
        callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      },
      (err) => {
        console.warn('[listenToParentStudents] web error:', err?.message);
        callback([]);
      }
    );
  },

  /**
   * Director del colegio: lista todos los estudiantes registrados para su
   * colegio. Consulta /students root filtrando por schoolId. Antes esta
   * conexión no existía → el dashboard del director veía "0 Estudiantes"
   * hardcoded aunque los padres inscribieran hijos.
   */
  listenToSchoolStudents(schoolId: string, callback: (students: any[]) => void) {
    if (Platform.OS !== 'web') {
      return nativeFirestore()
        .collection('students')
        .where('schoolId', '==', schoolId)
        .onSnapshot((snap: any) => {
          callback(snap.docs.map((d: any) => ({ id: d.id, ...d.data() })));
        }, (err: any) => { console.warn('[listenToSchoolStudents]', err?.message); callback([]); });
    }
    const q = query(collection(db, 'students'), where('schoolId', '==', schoolId));
    return onSnapshot(
      q,
      (snap) => {
        callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      },
      (err) => {
        console.warn('[listenToSchoolStudents] web error:', err?.message);
        callback([]);
      }
    );
  },

  /**
   * AUDIT FIX SCHOOL — Cuando el padre pulsa "Inscribir a mi Hijo" con un
   * driver específico, antes solo llamábamos markSchoolTrialStart (marca
   * el trial del driver). NO escribíamos `driverId` al estudiante. El
   * driver luego consultaba students where driverId=me → 0 resultados →
   * "no tengo estudiantes asignados". Ahora ambas escrituras se hacen en
   * paralelo: al student le ponemos driverId y al driver le arrancamos el
   * trial.
   *
   * Retorna void; el listener del padre y del driver detectan el cambio
   * automáticamente y sincronizan sus UIs.
   */
  async linkStudentToDriver(
    parentId: string,
    studentSubId: string,
    studentRootId: string,
    driverId: string,
    driverName?: string | null
  ) {
    const payload = {
      driverId,
      driver: driverName || 'Asignado',
      status: 'Esperando abordaje',
      linkedAt: serverTimestamp(),
    };
    const writes: Promise<any>[] = [];
    if (Platform.OS !== 'web') {
      writes.push(
        nativeFirestore().collection('users').doc(parentId).collection('students').doc(studentSubId).update(payload)
      );
      writes.push(
        nativeFirestore().collection('students').doc(studentRootId).update(payload)
      );
    } else {
      writes.push(updateDoc(doc(db, 'users', parentId, 'students', studentSubId), payload));
      writes.push(updateDoc(doc(db, 'students', studentRootId), payload));
    }
    await Promise.all(writes);
  },

  // Normaliza un nombre de colegio a una clave estable para búsqueda por ruta.
  // Mismo criterio que el schoolId de los estudiantes (línea ~1040).
  _schoolKey(name: string): string {
    return String(name ?? '').trim().toLowerCase().replace(/\s+/g, '-');
  },

  /**
   * FIX AUDIT SCHOOL — Modelo de BÚSQUEDA POR RUTA (decisión del fundador):
   * padres y directora buscan choferes escolares que sirvan la ruta/colegio del
   * niño. Antes se consultaba por `schoolId ==` (un solo colegio) y el parent
   * pasaba el NOMBRE mientras el driver guardaba el SLUG → nunca coincidían, y
   * exigía certificación del director (postulantes vacíos para siempre). Ahora:
   * el chofer declara los colegios/rutas que sirve (`schoolKeys[]`) y aquí
   * consultamos `schoolKeys array-contains key(colegio)`.
   */
  listenToSchoolDrivers(schoolNameOrKey: string, callback: (drivers: any[]) => void) {
    // Solo por `schoolKeys array-contains key`. NO filtramos por role: el modelo
    // de chofer escolar está entre dos esquemas (role SCHOOL_DRIVER vs
    // DRIVER+SCHOOL_BUS); quien declara su ruta (setSchoolDriverProfile) queda
    // descubrible sin importar la etiqueta exacta de rol.
    const key = this._schoolKey(schoolNameOrKey);
    if (Platform.OS !== 'web') {
      return nativeFirestore()
        .collection('users')
        .where('schoolKeys', 'array-contains', key)
        .onSnapshot((snap: any) => {
          callback(snap.docs.map((d: any) => ({ id: d.id, ...d.data() })));
        }, (err: any) => { console.warn('[listenToSchoolDrivers]', err?.message); callback([]); });
    }
    const q = query(
      collection(db, 'users'),
      where('schoolKeys', 'array-contains', key)
    );
    return onSnapshot(
      q,
      (snapshot) => {
        callback(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
      },
      (err) => {
        console.warn('[listenToSchoolDrivers] web error:', err?.message);
        callback([]);
      }
    );
  },

  /**
   * El chofer escolar declara su perfil de ruta: colegios que sirve + vehículo
   * + tarifas por zona. Lo guarda en su /users/{uid} para ser DESCUBRIBLE por
   * padres/directora. `schoolKeys` (normalizado) es lo que indexa la búsqueda;
   * `schools` guarda los nombres para mostrar.
   */
  async setSchoolDriverProfile(uid: string, data: {
    schools: string[];
    vehicle?: string;
    plate?: string;
    seats?: string;
    zones?: { A: number; B: number; C: number };
  }) {
    const schools = (data.schools || []).map((s) => String(s).trim()).filter(Boolean);
    const schoolKeys = schools.map((s) => this._schoolKey(s));
    const payload: any = {
      schools,
      schoolKeys,
      schoolDriverListed: schoolKeys.length > 0,
      updatedAt: serverTimestamp(),
    };
    if (data.vehicle !== undefined) payload.vehicle = data.vehicle;
    if (data.plate !== undefined) payload.plate = data.plate;
    if (data.seats !== undefined) payload.seats = data.seats;
    if (data.zones !== undefined) payload.zones = data.zones;
    if (Platform.OS !== 'web') {
      await nativeFirestore().collection('users').doc(uid).set(payload, { merge: true });
      return;
    }
    await setDoc(doc(db, 'users', uid), payload, { merge: true });
  },

  async certifySchoolDriver(driverId: string) {
    const driverRef = doc(db, 'users', driverId);
    await updateDoc(driverRef, { status: 'Certificado' });
  },

  async revokeSchoolDriver(driverId: string) {
    const driverRef = doc(db, 'users', driverId);
    await updateDoc(driverRef, { status: 'Revocado' });
  },

  // --- CRM / ENLACE MAGICO ---
  /**
   * Aprueba a un candidato del CRM del dueño.
   *
   * ANTES CREABA UN DOCUMENTO NUEVO (`agregarDoc`), y eso duplicaba: candidatos
   * y choferes aprobados viven en la MISMA subcolección
   * `users/{ownerId}/crm_drivers` y se separan por `status` (crm-settings.tsx:115).
   * El candidato original se quedaba con `status: 'PENDING_REVIEW'` para
   * siempre, así que el dueño tocaba "Aprobar", veía el aviso de que quedó
   * aprobado, recargaba la pantalla y el mismo candidato seguía en
   * "Solicitudes pendientes" — y podía aprobarlo otra vez, y otra.
   *
   * Ahora se ACTUALIZA el documento del candidato, que ya existe: lo escribe la
   * callable `submitCandidateOnboarding` (functions/src/drivers.ts) con el uid
   * del candidato como id. Con eso desaparece de pendientes, aparece en activos
   * y deja de multiplicarse.
   *
   * `vehicle` ya no se fuerza a 'Pendiente': si quien aprueba pasa un vehículo
   * asignado, se respeta (antes se descartaba el valor que traía driverData).
   */
  async onboardDriverToCRM(ownerId: string, candidateId: string, driverData: any) {
    return await actualizarDoc(['users', ownerId, 'crm_drivers', candidateId], {
      ...driverData,
      status: 'Aprobado',
      rating: '5.0',
      trips: 0,
      vehicle: driverData?.vehicle || 'Pendiente',
      joinedAt: new Date(),
    });
  },

  listenToCRMDrivers(ownerId: string, callback: (drivers: any[]) => void) {
    if (Platform.OS !== 'web') {
      return nativeFirestore()
        .collection('users')
        .doc(ownerId)
        .collection('crm_drivers')
        .onSnapshot((snap: any) => {
          callback(snap.docs.map((d: any) => ({ id: d.id, ...d.data() })));
        }, (err: any) => { console.warn('[listenToCRMDrivers]', err?.message); callback([]); });
    }
    const driversRef = collection(db, `users/${ownerId}/crm_drivers`);
    return onSnapshot(
      driversRef,
      (snapshot) => {
        const drivers = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        callback(drivers);
      },
      (err) => {
        console.warn('[listenToCRMDrivers] web error:', err?.message);
        callback([]);
      }
    );
  },

  // --- USUARIOS ---
  async updateUserProfile(uid: string, profileData: any) {
    // FIX módulo 5 — En móvil usamos el SDK nativo. El SDK web va anónimo
    // cuando el user entró por fallback REST (sin sesión nativa Firebase),
    // y la regla `isSelf(uid)` rechaza la escritura. El custom token
    // exchange en authBridge ya estableció la sesión nativa, así que
    // nativeFirestore() hereda el auth y la rule pasa.
    if (Platform.OS !== 'web') {
      await nativeFirestore()
        .collection('users')
        .doc(uid)
        .set(
          {
            ...profileData,
            updatedAt: nativeFirestore.FieldValue.serverTimestamp(),
          },
          { merge: true }
        );
      return;
    }
    const userRef = doc(db, 'users', uid);
    return await setDoc(userRef, {
      ...profileData,
      updatedAt: serverTimestamp()
    }, { merge: true });
  },

  /**
   * Lee el doc /users/{uid}. Usado para:
   *   - Verificar kycCompleted al abrir la app (_layout.tsx)
   *   - Mostrar nombre/avatar en pantallas de perfil
   *   - Chequear flags del chofer (driverStatus, etc.)
   *
   * FIX módulos 3 y 5 — Mismo patrón nativo. El SDK web fallaba para
   * users REST → null → _layout asume KYC pendiente → re-prompt infinito.
   */
  /**
   * LAS RECOMPENSAS REALES DE ESTA PERSONA.
   *
   * La pantalla de Recompensas las leía del store (`driverTripsThisMonth`,
   * `driverBonusAccrued`, `passengerTripsCompleted`…), y esos campos no los
   * escribe NADIE: nacen en 0 y se quedan en 0. Resultado medido en la
   * auditoría del 27-sep-2026: un chofer con viajes hechos y bonos ya
   * acreditados por el servidor abría Perfil → Recompensas y leía «0 / 250
   * Viajes» y «Bonos Generados: RD$ 0.00». Prometer dinero y enseñar cero es
   * peor que no enseñar nada.
   *
   * Los números de verdad viven en dos sitios y aquí se juntan:
   *   · /users/{uid} → `driverTripsThisMonth` y `driverTotalLifetimeTrips` los
   *     escribe `registrarViajeDeChofer` (functions/src/incentives.ts), y
   *     `loyalty.passengerTripsCompleted` / `loyalty.freeTripsAvailable` los
   *     escribe `processRideCompletion` (functions/src/loyalty.ts).
   *   · /driver_bonuses → un asiento por bono (referidos, meta mensual, hito).
   *     La regla deja leer SOLO los propios (firestore.rules:2431), y por eso
   *     la consulta filtra por `driverUid`: sin ese filtro, la lectura entera
   *     se deniega.
   *
   * Devuelve `null` si no se pudo leer, para que la pantalla pueda decir «no
   * pudimos cargarlo» en vez de pintar ceros que parecen un hecho.
   */
  async getRewardsSnapshot(uid: string): Promise<{
    passengerTripsCompleted: number;
    freeTripsAvailable: number;
    driverTripsThisMonth: number;
    driverTotalLifetimeTrips: number;
    bonusAccrued: number;
  } | null> {
    if (!uid) return null;
    try {
      const perfil = await this.getUserProfile(uid);
      if (!perfil) return null;
      const num = (v: any) => {
        const n = Number(v);
        return Number.isFinite(n) && n > 0 ? n : 0;
      };
      let bonusAccrued = 0;
      try {
        if (Platform.OS !== 'web') {
          const snap = await nativeFirestore()
            .collection('driver_bonuses')
            .where('driverUid', '==', uid)
            .limit(200)
            .get();
          snap.docs.forEach((d: any) => { bonusAccrued += num(d.data()?.amount); });
        } else {
          const snap = await getDocs(
            query(collection(db, 'driver_bonuses'), where('driverUid', '==', uid), limit(200))
          );
          snap.docs.forEach((d) => { bonusAccrued += num((d.data() as any)?.amount); });
        }
      } catch (e: any) {
        // Un pasajero no tiene bonos y la consulta puede denegarse: eso no
        // invalida el resto del informe, solo deja el bono en 0.
        console.warn('[recompensas] no se pudieron leer los bonos:', e?.message);
      }
      return {
        passengerTripsCompleted: num(perfil?.loyalty?.passengerTripsCompleted),
        freeTripsAvailable: num(perfil?.loyalty?.freeTripsAvailable),
        driverTripsThisMonth: num(perfil?.driverTripsThisMonth),
        driverTotalLifetimeTrips: num(perfil?.driverTotalLifetimeTrips),
        bonusAccrued: Math.round(bonusAccrued * 100) / 100,
      };
    } catch (e: any) {
      console.warn('[recompensas] no se pudo leer el perfil:', e?.message);
      return null;
    }
  },

  /**
   * LOS CONCURSOS ABIERTOS, para que el chofer los vea en la app.
   *
   * Los crea el fundador desde su panel (`adminCrearConcurso`) y las reglas
   * los dejan LEER a cualquiera con sesión: un concurso secreto no es un
   * concurso. Escribirlos no puede nadie desde un teléfono — el premio es
   * saldo real contra la cuota.
   *
   * Se filtran por fecha aquí y no en la consulta para no pedir un índice más
   * por un puñado de documentos: nunca habrá cientos de concursos abiertos.
   */
  async getConcursosActivos(): Promise<any[]> {
    const ahora = Date.now();
    const vigente = (c: any) => {
      const fin = c?.fin?.toMillis?.() ?? c?.fin?.toDate?.()?.getTime?.() ?? 0;
      const inicio = c?.inicio?.toMillis?.() ?? c?.inicio?.toDate?.()?.getTime?.() ?? 0;
      return (!inicio || inicio <= ahora) && (!fin || fin >= ahora);
    };
    try {
      if (Platform.OS !== 'web') {
        const snap = await nativeFirestore()
          .collection('concursos')
          .where('estado', '==', 'ACTIVO')
          .limit(10)
          .get();
        return snap.docs.map((d: any) => ({ id: d.id, ...d.data() })).filter(vigente);
      }
      const snap = await getDocs(
        query(collection(db, 'concursos'), where('estado', '==', 'ACTIVO'), limit(10))
      );
      return snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })).filter(vigente);
    } catch (e: any) {
      console.warn('[concursos] no se pudieron leer:', e?.message);
      return [];
    }
  },

  // ══════════════════════════════════════════════════════════════════════
  // BLOQUEAR Y REPORTAR EN EL CHAT
  //
  // Lo exige la política de contenido de usuarios de Google Play para cualquier
  // chat de uno a uno, y Go Safely tiene tres (viaje, pedido de comida y
  // negociación de Rent Car). El bloqueo vive en `users/{yo}/bloqueados/{otro}`
  // y lo hacen cumplir las REGLAS: con ese documento puesto, Firestore deniega
  // los mensajes en las DOS direcciones. Aquí solo se escribe la marca y se
  // consulta; la app nunca decide sola si un mensaje pasa.
  //
  // Se usan los ayudantes de este archivo (`guardarDoc`, `borrarDoc`,
  // `agregarDoc`) y no el SDK web directo: en nativo esa escritura llega como
  // anónima y la regla la deniega. Está dicho en la cabecera del archivo.
  // ══════════════════════════════════════════════════════════════════════

  /** Bloquea a alguien. Idempotente: bloquear dos veces no rompe nada. */
  async bloquearUsuario(yo: string, otroUid: string, contexto: string, referenciaId: string | null) {
    if (!yo) throw new Error('Necesitas tener la sesión abierta para bloquear.');
    if (!otroUid || otroUid === yo) throw new Error('No puedes bloquearte a ti mismo.');
    await guardarDoc(['users', yo, 'bloqueados', otroUid], {
      contexto: String(contexto || ''),
      referenciaId: referenciaId || null,
      creadoEn: selloServidor(),
    });
  },

  /** Quita el bloqueo. Lo que ya se escribió en el chat no se toca. */
  async desbloquearUsuario(yo: string, otroUid: string) {
    if (!yo || !otroUid) return;
    await borrarDoc(['users', yo, 'bloqueados', otroUid]);
  },

  /** ¿Lo bloqueé YO? Es lo que decide qué botón se pinta. */
  async loBloquee(yo: string, otroUid: string): Promise<boolean> {
    if (!yo || !otroUid) return false;
    try {
      if (Platform.OS !== 'web') {
        const s: any = await nativeFirestore()
          .collection('users').doc(yo).collection('bloqueados').doc(otroUid).get();
        return typeof s.exists === 'function' ? s.exists() : !!s.exists;
      }
      const s = await getDoc(doc(db, 'users', yo, 'bloqueados', otroUid));
      return s.exists();
    } catch {
      return false;
    }
  },

  /**
   * ¿Hay bloqueo en CUALQUIERA de los dos sentidos?
   *
   * Al que fue bloqueado también hay que cerrarle la caja de texto: si no,
   * escribe, el servidor lo deniega y para él la app se rompió sin explicación.
   * Su propio documento de bloqueo no lo puede leer —es privado del otro—, así
   * que se le pregunta al servidor por el RESULTADO (`chatBloqueado`), no por
   * el documento.
   */
  async hayBloqueoEntre(yo: string, otroUid: string): Promise<boolean> {
    if (!yo || !otroUid) return false;
    if (await this.loBloquee(yo, otroUid)) return true;
    try {
      const { callChatBloqueado } = await import('./cloudFunctions');
      const r: any = await callChatBloqueado({ otroUid });
      return !!r?.data?.bloqueado;
    } catch {
      // Sin respuesta del servidor NO se cierra la caja: es mejor dejar
      // escribir y que el servidor deniegue, que callar a alguien por un fallo
      // de red.
      return false;
    }
  },

  /** Un reporte que mira una persona. No borra nada ni bloquea por su cuenta. */
  async reportarUsuario(datos: {
    yo: string;
    otroUid: string;
    otroNombre: string;
    contexto: string;
    referenciaId: string | null;
    motivo: string;
  }) {
    const yo = String(datos.yo || '');
    if (!yo) throw new Error('Necesitas tener la sesión abierta para reportar.');
    await agregarDoc(['reportes_usuarios'], {
      reportanteUid: yo,
      reportadoUid: String(datos.otroUid || ''),
      reportadoNombre: String(datos.otroNombre || '').slice(0, 80),
      contexto: String(datos.contexto || ''),
      referenciaId: datos.referenciaId || null,
      motivo: String(datos.motivo || '').slice(0, 500),
      revisado: false,
      creadoEn: selloServidor(),
    });
  },

  async getUserProfile(uid: string): Promise<any | null> {
    if (Platform.OS !== 'web') {
      const snap: any = await nativeFirestore().collection('users').doc(uid).get();
      return snap.exists() ? snap.data() : null;
    }
    const snap = await getDoc(doc(db, 'users', uid));
    return snap.exists() ? snap.data() : null;
  },

  /**
   * El documento del negocio de este uid, o null si no tiene.
   *
   * SU EXISTENCIA ES LA PRUEBA de que el negocio esta registrado de verdad:
   * ese documento solo lo crea el registro de negocio (el `allow create` de
   * /restaurants), y es exactamente la misma prueba que usa el servidor para
   * decidir si le deja cambiar de modo (functions/src/modoDeUsuario.ts:98).
   * El perfil la necesita para no ensenarle «Cambiar a Pasajero» a un dueno
   * registrado — y para SI ensenarsela a quien solo se asomo, que es su unica
   * salida.
   */
  async getRestaurantById(ownerId: string): Promise<any | null> {
    if (Platform.OS !== 'web') {
      const snap: any = await nativeFirestore().collection('restaurants').doc(ownerId).get();
      return snap.exists() ? { id: snap.id, ...snap.data() } : null;
    }
    const snap = await getDoc(doc(db, 'restaurants', ownerId));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
  },

  /**
   * ═══════════════════════════════════════════════════════════════════════
   * CAUSA RAÍZ del rechazo de Google Play (vc13 y vc16: "app does not load")
   * ═══════════════════════════════════════════════════════════════════════
   *
   * Al registrarse por email, `auth.tsx` llamaba directo a
   * createUserWithEmailAndPassword y NUNCA creaba el doc /users/{uid}.
   * `authService.registerUser()` — la única función del proyecto que sí lo
   * creaba — no se invocaba desde NINGUNA parte (0 referencias en todo src/).
   *
   * Cadena de consecuencias:
   *   1. getUserProfile() devuelve null para TODO usuario nuevo.
   *   2. El guard de _layout/auth lee kycCompleted → undefined → trata al
   *      usuario como KYC pendiente para siempre. Nunca llega al mapa.
   *   3. PEOR: las reglas de Firestore hacen get() sobre /users/{uid} dentro
   *      de myRole()/hasRole(). Si el doc NO existe, ese get() falla y la
   *      regla DENIEGA en cascada → permission-denied en /driverLocations,
   *      /rides y demás → pantallas vacías → el revisor de Google ve
   *      literalmente "la app no carga".
   *
   * Esta función crea el doc si falta. Es IDEMPOTENTE: si ya existe, no toca
   * nada. Se llama al registrar Y al iniciar sesión, para curar también a los
   * usuarios que ya se registraron sin doc — incluida la cuenta de prueba que
   * se le entregó a Google.
   *
   * Usa el SDK NATIVO en móvil: el web SDK va anónimo cuando la sesión viene
   * de getNativeAuth(), y la regla `allow create: if isSelf(uid)` lo
   * rechazaría por request.auth == null.
   */
  async ensureUserProfile(
    uid: string,
    seed: { email?: string | null; phone?: string | null; name?: string | null; role?: string | null } = {}
    /** true si el documento se CREO en esta llamada (cuenta nueva); false si ya existia
     *  o si no se pudo saber. Lo usa auth.tsx para pedirle a quien acaba de entrar con
     *  Google que complete su perfil, y SOLO a ese: quien ya esta dentro no se toca. */
  ): Promise<boolean> {
    if (!uid) return false;

    // ⚠️ NO incluir campos protegidos. `firestore.rules:135-145` deniega el
    // create si el payload trae CUALQUIERA de estos:
    //   isDriverVerified, driverVerifiedAt, kycCompleted, kycCompletedAt,
    //   isVerified, kycApproved, conductPaperStatus, walletBalance,
    //   driverStatus, penaltyBlockActive, stripe*, schoolId, referredBy…
    // Un set() sobre un doc inexistente ES un create, así que mandarlos
    // (aunque sea en false) hacía que la regla denegara SIEMPRE — y el catch
    // de abajo se lo tragaba en silencio. Ese era el bug.
    //
    // No hace falta escribirlos: su ausencia ya equivale a "no verificado"
    // (`!!(profile?.kycCompleted ?? profile?.isVerified)` → false). Lo único
    // que importa es que el DOCUMENTO EXISTA, para que myRole()/hasRole() de
    // las reglas no fallen y dejen de denegar en cascada.
    const build = (ts: any) => ({
      email: seed.email ?? null,
      phone: seed.phone ?? null,
      name: seed.name ?? '',
      role: seed.role ?? 'PASSENGER',
      baseRole: seed.role ?? 'PASSENGER',
      createdAt: ts,
    });

    // Timeout duro: si Firestore no responde, no dejamos el login colgado.
    const conTimeout = <T,>(p: Promise<T>, ms = 8000) =>
      Promise.race([p, new Promise<null>((r) => setTimeout(() => r(null), ms))]);

    try {
      if (Platform.OS !== 'web') {
        const ref = nativeFirestore().collection('users').doc(uid);
        const snap: any = await conTimeout(ref.get());
        // ⚠️ `conTimeout` RESUELVE a null a los 8 s, NO lanza — asi que el
        // catch de abajo no cubre esto. Sin distinguirlo, una lectura lenta se
        // leia como «el documento no existe» y la funcion devolvia `true`: a
        // un usuario VIEJO con mala senal se le enseñaba «Completa tu
        // perfil». Se intenta crear igual (es lo que se hacia antes y no
        // estorba, el set lleva merge), pero NO se afirma que sea nuevo.
        const noSePudoLeer = snap === null;
        if (snap && snap.exists && snap.exists()) return false; // ya existe
        await conTimeout(
          ref.set(build(nativeFirestore.FieldValue.serverTimestamp()), { merge: true })
        );
        console.warn('[ensureUserProfile] doc creado para', uid);
        return !noSePudoLeer;
      }
      const ref = doc(db, 'users', uid);
      const snap: any = await conTimeout(getDoc(ref));
      const noSePudoLeerWeb = snap === null;
      if (snap && snap.exists && snap.exists()) return false;
      await conTimeout(setDoc(ref, build(serverTimestamp()), { merge: true }));
      console.warn('[ensureUserProfile] doc creado (web) para', uid);
      return !noSePudoLeerWeb;
    } catch (e: any) {
      // No relanzamos: si esto falla, preferimos que el login continúe y el
      // guard mande a KYC, en vez de dejar al usuario en un spinner muerto.
      console.warn('[ensureUserProfile] falló:', e?.message ?? e);
      // Falla CERRADO para el paso de «completa tu perfil»: si no sabemos si la
      // cuenta es nueva, NO se interrumpe a nadie. Un falso positivo aqui seria
      // mandar a completar el perfil a quien ya lo tiene hecho.
      return false;
    }
  },

  /**
   * MULTA POR CANCELACIONES — Escucha en vivo los campos de penalización del
   * propio usuario (penaltyBlockActive, cancellationFineOwed, cancellationCount)
   * para que el PenaltyGate se muestre/oculte al instante cuando la Cloud
   * Function bloquea o el backend desbloquea tras pagar. Solo el propio user
   * puede leer su doc (allow read: isSelf), así que no hay fuga de privacidad.
   */
  listenToUserPenalty(
    uid: string,
    callback: (p: { penaltyBlockActive: boolean; cancellationFineOwed: number; cancellationCount: number }) => void
  ) {
    const emit = (data: any) => {
      callback({
        penaltyBlockActive: data?.penaltyBlockActive === true,
        cancellationFineOwed: Number(data?.cancellationFineOwed || 0),
        cancellationCount: Number(data?.cancellationCount || 0),
      });
    };
    if (Platform.OS !== 'web') {
      return nativeFirestore().collection('users').doc(uid).onSnapshot(
        (snap: any) => emit(snap?.exists?.() ? snap.data() : {}),
        (err: any) => { console.warn('[listenToUserPenalty]', err?.message); emit({}); }
      );
    }
    return onSnapshot(
      doc(db, 'users', uid),
      (snap) => emit(snap.exists() ? snap.data() : {}),
      (err) => {
        console.warn('[listenToUserPenalty] web error:', err?.message);
        emit({});
      }
    );
  },

  /**
   * BANNER DE PRUEBA 15 DÍAS — Escucha en vivo el estado del trial del propio
   * chofer y calcula los días restantes desde el `driverTrialEndsAt` REAL de
   * Firestore. ANTES el store tenía `driverTrialDaysLeft` hardcodeado en 14 y
   * NADIE lo sincronizaba → el banner de profile.tsx (recordatorio del Papel
   * de Buena Conducta al terminar la prueba) NUNCA se mostraba. Usa
   * nativeFirestore en device (sesión autenticada), igual que listenToUserPenalty.
   */
  listenToDriverTrial(
    uid: string,
    callback: (info: { driverStatus: string | null; daysLeft: number | null; blockedNoPayment: boolean }) => void
  ) {
    const DAY_MS = 24 * 60 * 60 * 1000;
    const toMillis = (v: any): number | null => {
      if (!v) return null;
      if (typeof v.toMillis === 'function') return v.toMillis();
      if (typeof v.seconds === 'number') return v.seconds * 1000; // Timestamp serializado
      if (typeof v._seconds === 'number') return v._seconds * 1000;
      const t = new Date(v).getTime();
      return isNaN(t) ? null : t;
    };
    const emit = (data: any) => {
      const endsMs = toMillis(data?.driverTrialEndsAt);
      const daysLeft = endsMs != null ? Math.max(0, Math.ceil((endsMs - Date.now()) / DAY_MS)) : null;
      callback({
        driverStatus: (data?.driverStatus as string) ?? null,
        daysLeft,
        blockedNoPayment: data?.driverBlockedNoPayment === true,
      });
    };
    if (Platform.OS !== 'web') {
      return nativeFirestore().collection('users').doc(uid).onSnapshot(
        (snap: any) => emit(snap?.exists?.() ? snap.data() : {}),
        (err: any) => { console.warn('[listenToDriverTrial]', err?.message); emit({}); }
      );
    }
    return onSnapshot(
      doc(db, 'users', uid),
      (snap) => emit(snap.exists() ? snap.data() : {}),
      (err) => {
        console.warn('[listenToDriverTrial] web error:', err?.message);
        emit({});
      }
    );
  },

  // ── CAMPANA DE NOTIFICACIONES (Bug #5) ──────────────────────────────────
  // El backend (sendPushToUid) guarda cada notificación en
  // /users/{uid}/notifications. Aquí la app la lee en vivo, marca leídas y borra.
  listenToUserNotifications(uid: string, callback: (items: any[]) => void) {
    const map = (docs: any[]) => docs.map((d: any) => ({ id: d.id, ...(typeof d.data === 'function' ? d.data() : d) }));
    if (Platform.OS !== 'web') {
      return nativeFirestore()
        .collection('users').doc(uid).collection('notifications')
        .orderBy('createdAt', 'desc').limit(50)
        .onSnapshot(
          (snap: any) => callback(snap.docs.map((d: any) => ({ id: d.id, ...d.data() }))),
          (err: any) => { console.warn('[listenToUserNotifications]', err?.message); callback([]); }
        );
    }
    const q = query(collection(db, 'users', uid, 'notifications'), orderBy('createdAt', 'desc'), limit(50));
    return onSnapshot(
      q,
      (snap) => callback(map(snap.docs)),
      (err) => {
        console.warn('[listenToUserNotifications] web error:', err?.message);
        callback([]);
      }
    );
  },

  async markNotificationRead(uid: string, notifId: string) {
    if (Platform.OS !== 'web') {
      await nativeFirestore().collection('users').doc(uid).collection('notifications').doc(notifId).update({ read: true });
      return;
    }
    await updateDoc(doc(db, 'users', uid, 'notifications', notifId), { read: true });
  },

  async markAllNotificationsRead(uid: string, ids: string[]) {
    await Promise.all(ids.map((id) => this.markNotificationRead(uid, id).catch(() => {})));
  },

  async deleteNotification(uid: string, notifId: string) {
    if (Platform.OS !== 'web') {
      await nativeFirestore().collection('users').doc(uid).collection('notifications').doc(notifId).delete();
      return;
    }
    await deleteDoc(doc(db, 'users', uid, 'notifications', notifId));
  },

  // Contador de NO-leídas en vivo, para el badge de la campana (antes estaba
  // siempre encendido/hardcodeado). Query de un solo campo (where read==false)
  // → índice automático, sin composite.
  listenToUnreadNotificationCount(uid: string, callback: (count: number) => void) {
    if (Platform.OS !== 'web') {
      return nativeFirestore()
        .collection('users').doc(uid).collection('notifications')
        .where('read', '==', false)
        .onSnapshot((snap: any) => callback(snap.size ?? 0), (err: any) => { console.warn('[unreadNotif]', err?.message); callback(0); });
    }
    const q = query(collection(db, 'users', uid, 'notifications'), where('read', '==', false));
    return onSnapshot(
      q,
      (snap) => callback(snap.size ?? 0),
      (err) => {
        console.warn('[listenToUnreadNotificationCount] web error:', err?.message);
        callback(0);
      }
    );
  },

  async createFoodOrder(orderData: any) {
    return await addDoc(collection(db, 'food_orders'), {
      ...orderData,
      createdAt: serverTimestamp(),
      status: 'PENDING' // PENDING, PREPARING, READY, ASSIGNED, PICKED_UP, DELIVERED
    });
  },

  async createRestaurant(ownerId: string, data: any) {
    // El doc del restaurante usa el uid del owner como id (un restaurante por dueño).
    // FIX BLOQUEADOR (auditoría) — En móvil el SDK web va anónimo (auth vive en
    // el SDK nativo) y la regla rechaza la escritura → el restaurante no podía
    // registrarse. nativeFirestore() hereda la sesión nativa. Mismo patrón que
    // updateUserProfile/updateRideStatus.
    if (Platform.OS !== 'web') {
      await nativeFirestore().collection('restaurants').doc(ownerId).set({
        ...data,
        ownerId,
        createdAt: nativeFirestore.FieldValue.serverTimestamp(),
        updatedAt: nativeFirestore.FieldValue.serverTimestamp(),
      }, { merge: true });
      return ownerId;
    }
    const ref = doc(db, 'restaurants', ownerId);
    await setDoc(ref, {
      ...data,
      ownerId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }, { merge: true });
    return ref.id;
  },

  /**
   * @deprecated Trae TODOS los restaurantes — no escala a >500. Usar
   * `listenToRestaurantsPaginated` o `fetchRestaurantsPage` en producción.
   * Lo mantenemos por compatibilidad con pantallas legacy.
   */
  listenToRestaurants(callback: (restaurants: any[]) => void) {
    if (Platform.OS !== 'web') {
      return nativeFirestore()
        .collection('restaurants')
        .where('status', '==', 'APPROVED')
        .onSnapshot((snap: any) => {
          callback(snap.docs.map((d: any) => ({ id: d.id, ...d.data() })));
        }, (err: any) => { console.warn('[listenToRestaurants]', err?.message); callback([]); });
    }
    const q = query(collection(db, 'restaurants'), where('status', '==', 'APPROVED'));
    return onSnapshot(
      q,
      (snapshot) => {
        const restaurants = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        callback(restaurants);
      },
      (err) => {
        console.warn('[listenToRestaurants] web error:', err?.message);
        callback([]);
      }
    );
  },

  /**
   * Listener paginado con filtros server-side.
   * Devuelve los primeros `pageSize` restaurantes APPROVED.
   *
   * - Premium primero (orderBy isPremium desc)
   * - Después por rating descendente
   * - Filtro opcional por category (si se pasa)
   *
   * Requiere índice compuesto en Firestore (status + category + isPremium + rating)
   * que se crea automáticamente la 1ra vez que falla la query (Firebase manda link).
   */
  listenToRestaurantsPaginated(
    opts: { pageSize?: number; category?: string | null },
    callback: (restaurants: any[]) => void
  ) {
    const pageSize = opts.pageSize ?? 30;
    // FUGA CERRADA (auditoría P0) — El respaldo sin orderBy se creaba dentro
    // del callback de error y su cancelador se TIRABA: la pantalla solo
    // recibía el cancelador de la consulta original, que ya estaba muerta, así
    // que el respaldo seguía escuchando después de desmontarse la pantalla y
    // se sumaba uno nuevo cada vez que se volvía a entrar. Mismo patrón que ya
    // funciona en (tabs)/activity.tsx: un solo cancelador mutable + una
    // bandera de "ya me cerraron" para el respaldo que nace tarde.
    let cancelar: (() => void) | null = null;
    let cancelada = false;
    let reintentado = false;
    const cerrar = () => {
      cancelada = true;
      try { cancelar?.(); } catch { /* ya estaba cerrada */ }
      cancelar = null;
    };

    if (Platform.OS !== 'web') {
      // Respaldo sin orderBy, por si falta el índice compuesto.
      const respaldoSimple = (): (() => void) => {
        let qFb: any = nativeFirestore()
          .collection('restaurants')
          .where('status', '==', 'APPROVED');
        if (opts.category) qFb = qFb.where('category', '==', opts.category);
        qFb = qFb.limit(pageSize);
        return qFb.onSnapshot((snap: any) => {
          callback(snap.docs.map((d: any) => ({ id: d.id, ...d.data() })));
        }, (e2: any) => {
          // Último recurso: si hasta el fallback simple falla, hay que
          // avisarle a la pantalla igual. Sin esto el catálogo de comida
          // se queda con el spinner girando para siempre — y esta es la
          // pantalla principal del rol FOOD_DELIVERY, que el revisor de
          // Google va a abrir con el modo invitado.
          console.warn('[listenToRestaurantsPaginated] fallback también falló:', e2?.message);
          callback([]);
        }) as () => void;
      };

      let q: any = nativeFirestore()
        .collection('restaurants')
        .where('status', '==', 'APPROVED');
      if (opts.category) q = q.where('category', '==', opts.category);
      q = q.orderBy('isPremium', 'desc').limit(pageSize);
      const inicial = q.onSnapshot((snap: any) => {
        callback(snap.docs.map((d: any) => ({ id: d.id, ...d.data() })));
      }, (err: any) => {
        console.warn('[listenToRestaurantsPaginated nativo] fallback simple:', err?.message);
        if (reintentado) { callback([]); return; }
        reintentado = true;
        const respaldo = respaldoSimple();
        // Si la pantalla ya se desmontó mientras el error viajaba, el respaldo
        // nace huérfano: hay que cerrarlo en el acto o queda vivo para siempre.
        if (cancelada) { try { respaldo(); } catch { /* nada */ } }
        else cancelar = respaldo;
      }) as () => void;
      // Si el error hubiera llegado SÍNCRONO, `cancelar` ya trae el respaldo:
      // pisarlo aquí lo dejaría huérfano.
      if (!reintentado && !cancelada) cancelar = inicial;
      else { try { inicial(); } catch { /* nada */ } }
      return cerrar;
    }
    const { limit: qLimit, orderBy: qOrderBy } = require('firebase/firestore');
    const constraints: any[] = [where('status', '==', 'APPROVED')];
    if (opts.category) {
      constraints.push(where('category', '==', opts.category));
    }
    constraints.push(qOrderBy('isPremium', 'desc'));
    constraints.push(qLimit(pageSize));
    const q = query(collection(db, 'restaurants'), ...constraints);
    const respaldoWeb = (): (() => void) => {
      const simple = query(
        collection(db, 'restaurants'),
        where('status', '==', 'APPROVED'),
        qLimit(pageSize)
      );
      return onSnapshot(
        simple,
        (snap) => {
          const rs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
          callback(rs);
        },
        (e2) => {
          console.warn('[listenToRestaurantsPaginated] fallback también falló:', e2?.message);
          callback([]);
        }
      );
    };
    const inicialWeb = onSnapshot(q, (snapshot) => {
      const restaurants = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      callback(restaurants);
    }, (err) => {
      console.warn('[listenToRestaurantsPaginated] fallback simple:', err?.message);
      // Misma fuga que en la rama nativa: el respaldo se creaba y su
      // cancelador se perdía.
      if (reintentado) { callback([]); return; }
      reintentado = true;
      const respaldo = respaldoWeb();
      if (cancelada) { try { respaldo(); } catch { /* nada */ } }
      else cancelar = respaldo;
    });
    if (!reintentado && !cancelada) cancelar = inicialWeb;
    else { try { inicialWeb(); } catch { /* nada */ } }
    return cerrar;
  },

  /**
   * Fetch puntual (sin listener) para la siguiente página al hacer scroll.
   * Devuelve { items, lastDoc } — pasar lastDoc al siguiente call para
   * cursor pagination.
   */
  async fetchRestaurantsPage(opts: {
    pageSize?: number;
    category?: string | null;
    lastDoc?: any;
  }): Promise<{ items: any[]; lastDoc: any | null }> {
    const { limit: qLimit, orderBy: qOrderBy, startAfter, getDocs } = require('firebase/firestore');
    const pageSize = opts.pageSize ?? 20;
    const constraints: any[] = [where('status', '==', 'APPROVED')];
    if (opts.category) constraints.push(where('category', '==', opts.category));
    constraints.push(qOrderBy('isPremium', 'desc'));
    if (opts.lastDoc) constraints.push(startAfter(opts.lastDoc));
    constraints.push(qLimit(pageSize));
    const q = query(collection(db, 'restaurants'), ...constraints);
    const snap = await getDocs(q);
    return {
      items: snap.docs.map((d: any) => ({ id: d.id, ...d.data() })),
      lastDoc: snap.docs[snap.docs.length - 1] ?? null,
    };
  },

  listenToRestaurantById(restaurantId: string, callback: (restaurant: any | null) => void) {
    if (Platform.OS !== 'web') {
      return nativeFirestore().collection('restaurants').doc(restaurantId).onSnapshot((docSnap: any) => {
        if (docSnap.exists()) callback({ id: docSnap.id, ...docSnap.data() });
        else callback(null);
      }, (err: any) => { console.warn('[listenToRestaurantById]', err?.message); callback(null); });
    }
    const docRef = doc(db, 'restaurants', restaurantId);
    return onSnapshot(
      docRef,
      (docSnap) => {
        if (docSnap.exists()) {
          callback({ id: docSnap.id, ...docSnap.data() });
        } else {
          callback(null);
        }
      },
      (err) => {
        console.warn('[listenToRestaurantById] web error:', err?.message);
        callback(null);
      }
    );
  },

  listenToRestaurantPromos(restaurantId: string, callback: (promos: any[]) => void) {
    if (Platform.OS !== 'web') {
      return nativeFirestore().collection('restaurants').doc(restaurantId).onSnapshot((docSnap: any) => {
        if (docSnap.exists() && docSnap.data().activePromotions) {
          callback(docSnap.data().activePromotions);
        } else {
          callback([]);
        }
      }, (err: any) => { console.warn('[listenToRestaurantPromos]', err?.message); callback([]); });
    }
    const promoRef = doc(db, 'restaurants', restaurantId);
    return onSnapshot(
      promoRef,
      (docSnap) => {
        if (docSnap.exists() && docSnap.data().activePromotions) {
          callback(docSnap.data().activePromotions);
        } else {
          callback([]);
        }
      },
      (err) => {
        console.warn('[listenToRestaurantPromos] web error:', err?.message);
        callback([]);
      }
    );
  },

  /**
   * @deprecated Las reglas Firestore ya no permiten leer /users/{driverId} directo.
   * Para el pasajero: usar `callGetDriverPublicInfo({driverId, rideId})` desde
   * cloudFunctions.ts. Devuelve los campos públicos + bankAccounts si hay ride
   * activo. Mantengo este método como no-op para no romper imports legacy.
   */
  listenToDriverProfile(_driverId: string, callback: (profile: any) => void) {
    callback(null);
    return () => {};
  },

  /**
   * CUENTAS BANCARIAS DEL PROPIO CHOFER — lectura en vivo.
   *
   * EL FALLO QUE ESTO CIERRA
   * La pantalla `driver-bank-accounts.tsx` leía con `listenToDriverProfile`,
   * que está deprecada y devuelve `null` siempre. Resultado: la lista salía
   * VACÍA aunque el chofer tuviera cuentas guardadas, y al añadir una nueva se
   * escribía `[nueva]` encima del array del servidor → cada cuenta nueva
   * BORRABA las anteriores. Es la pantalla por la que cobra.
   *
   * Aquí no hay problema de privacidad (que es lo que deprecó a la otra): el
   * chofer lee SU PROPIO doc, y la regla `allow read: isSelf(uid)` lo permite.
   * Rama por plataforma porque en Android el SDK web va sin sesión — mismo
   * patrón que `listenToUserPenalty` / `listenToDriverTrial`.
   *
   * El callback recibe SIEMPRE un array (vacío si no hay nada o si la lectura
   * falla), nunca `null`: la pantalla distingue "cargando" con su propio estado.
   */
  listenToMyBankAccounts(uid: string, callback: (accounts: any[]) => void) {
    const emit = (data: any) => {
      const lista = data?.bankAccounts;
      callback(Array.isArray(lista) ? lista : []);
    };
    if (Platform.OS !== 'web') {
      return nativeFirestore().collection('users').doc(uid).onSnapshot(
        (snap: any) => emit(snap?.exists?.() ? snap.data() : {}),
        (err: any) => { console.warn('[listenToMyBankAccounts]', err?.message); emit({}); }
      );
    }
    return onSnapshot(
      doc(db, 'users', uid),
      (snap) => emit(snap.exists() ? snap.data() : {}),
      (err) => {
        console.warn('[listenToMyBankAccounts] web error:', err?.message);
        emit({});
      }
    );
  },

  /**
   * @deprecated PISA la lista entera. Solo es correcto cuando quien llama
   * acaba de leer el array del SERVIDOR. Para añadir o quitar una cuenta usa
   * `addMyBankAccount` / `removeMyBankAccount`, que leen dentro de una
   * transacción y por eso no pueden borrar lo que la pantalla no vio.
   */
  async updateDriverBankAccounts(driverId: string, bankAccounts: any[]) {
    await actualizarDoc(['users', driverId], {
      bankAccounts,
      updatedAt: selloServidor(),
    });
  },

  /**
   * Lee el array de cuentas que hay AHORA EN EL SERVIDOR, le aplica `cambiar`
   * y lo vuelve a escribir, todo dentro de una transacción.
   *
   * Por qué transacción y no `arrayUnion`: `arrayUnion` no sabe quitar por id
   * (compara el objeto entero), así que el borrado necesitaría otro mecanismo
   * distinto al alta. Con una transacción, alta y baja usan el mismo camino y
   * ninguna de las dos puede partir de una lista obsoleta de la pantalla.
   */
  async _mutarCuentasBancarias(uid: string, cambiar: (actuales: any[]) => any[]) {
    const normalizar = (data: any): any[] => {
      const lista = data?.bankAccounts;
      return Array.isArray(lista) ? lista : [];
    };
    if (Platform.OS !== 'web') {
      const ref = nativeFirestore().collection('users').doc(uid);
      await nativeFirestore().runTransaction(async (tx: any) => {
        const snap = await tx.get(ref);
        const actuales = snap?.exists?.() ? normalizar(snap.data()) : [];
        tx.set(
          ref,
          {
            bankAccounts: cambiar(actuales),
            updatedAt: nativeFirestore.FieldValue.serverTimestamp(),
          },
          { merge: true }
        );
      });
      return;
    }
    const ref = doc(db, 'users', uid);
    await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      const actuales = snap.exists() ? normalizar(snap.data()) : [];
      tx.set(
        ref,
        { bankAccounts: cambiar(actuales), updatedAt: serverTimestamp() },
        { merge: true }
      );
    });
  },

  /** Añade una cuenta SOBRE lo que hay en el servidor. Devuelve la lista final. */
  async addMyBankAccount(uid: string, account: any): Promise<void> {
    await firestoreService._mutarCuentasBancarias(uid, (actuales) => {
      // Si el mismo id ya está (doble toque en "Guardar", reintento de red),
      // lo sustituimos en vez de duplicarlo.
      const sinRepetir = actuales.filter((c: any) => c?.id !== account?.id);
      return [...sinRepetir, account];
    });
  },

  /** Quita una cuenta por id de lo que hay en el servidor. */
  async removeMyBankAccount(uid: string, accountId: string): Promise<void> {
    await firestoreService._mutarCuentasBancarias(uid, (actuales) =>
      actuales.filter((c: any) => c?.id !== accountId)
    );
  },

  /**
   * ANTI-STALKING (auditoría 2026-07) — Marca en /driverLocations/{driverId}
   * QUIÉN tiene derecho a ver esa posición ahora mismo (el pasajero del viaje
   * activo). Antes la regla permitía leer a CUALQUIER cuenta con rol PASSENGER,
   * así que se podía volcar la colección entera y seguir en vivo a toda la
   * flota — incluidos los autobuses escolares con niños.
   *
   * Lo escribe el propio chofer (la regla exige isSelf), al aceptar y al cerrar
   * el viaje. Hoy es el RESPALDO: el que concede el permiso de verdad es el
   * servidor (`congelarDatosDelViaje`), que no depende de que este teléfono
   * esté vivo. Esta llave cubre el hueco si el disparador se retrasa.
   *
   * ⚠️ ARREGLO 2026-09 — ESTO ESCRIBÍA UN PERMISO SIN FECHA DE FIN.
   * El payload era `{ activePassengerId }` a secas, y la regla trataba el sello
   * AUSENTE como vigente. O sea que esta llave, una vez escrita, autorizaba
   * PARA SIEMPRE: el ex-pasajero podía seguir viendo la posición en vivo de su
   * chofer semanas después de que el viaje terminara. La limpieza dependía de
   * que esta misma app llegara a llamar aquí con `null` al terminar — y una app
   * que se cierra, se queda en segundo plano, se queda sin batería o se
   * desinstala a mitad de viaje NO LLAMA NUNCA. Un permiso cuya caducidad
   * depende de que un teléfono ajeno se porte bien no es una caducidad.
   *
   * Ahora la llave nace con su vencimiento, igual que la del servidor. Y la
   * regla ya no se inventa uno cuando falta (ver `permisoVigente()` en
   * firestore.rules): las dos mitades tienen que estar puestas o no hay mapa,
   * que es exactamente lo que se quiere.
   *
   * POR QUÉ 6 h: es la misma ventana que usa el servidor (VENTANA_PERMISO_MS en
   * functions/src/rides.ts) y por la misma razón — tiene que ser MÁS que el
   * viaje real más largo (el trayecto nacional no llega a 5 h; si vence a
   * mitad de camino el pasajero pierde el carro de la pantalla, que es el fallo
   * que esto viene a arreglar) y lo MENOS posible por encima de eso. Si esos
   * dos números se separan, el respaldo deja de comportarse como lo que
   * respalda. Se refresca solo: mientras el viaje esté vivo, cada paso del
   * viaje vuelve a pasar por aquí y por el disparador.
   *
   * `null` limpia el permiso: borra el destinatario Y el vencimiento, para no
   * dejar un sello suelto de un viaje que ya no existe.
   *
   * ── POR QUÉ MIRA ANTES DE ESCRIBIR ──────────────────────────────────────
   * El sello del servidor lo pone el reloj DEL SERVIDOR, que es el mismo con el
   * que la regla lo compara. El nuestro lo pone el reloj del teléfono. Si
   * pisáramos el suyo con el nuestro, un teléfono con la hora atrasada
   * ACORTARÍA un permiso perfectamente válido y dejaría a la pasajera sin mapa
   * — justo el fallo que este respaldo viene a evitar. Y con la hora adelantada
   * lo ALARGARÍA, que es el acecho. Así que si ya hay un permiso vivo para esta
   * misma persona, no se toca: manda el del servidor. Solo escribimos cuando no
   * hay ninguno, que es el único momento en que este respaldo sirve de algo.
   * (Es el mismo criterio de idempotencia que usa `sincronizarPermisoDeUbicacion`.)
   */
  async setDriverActivePassenger(driverId: string, passengerId: string | null) {
    if (!driverId) return;

    if (passengerId) {
      let yaHayPermisoVivo = false;
      try {
        const previo: any =
          Platform.OS !== 'web'
            ? (await nativeFirestore().collection('driverLocations').doc(driverId).get())?.data()
            : (await getDoc(doc(db, 'driverLocations', driverId))).data();
        const hasta = previo?.activePassengerUntil;
        const ms = typeof hasta?.toMillis === 'function' ? hasta.toMillis() : 0;
        yaHayPermisoVivo =
          previo?.activePassengerId === passengerId && Number.isFinite(ms) && ms > Date.now();
      } catch (e: any) {
        // Si no se puede leer, se escribe igual: un permiso puesto por nosotros
        // es peor que el del servidor, pero infinitamente mejor que ninguno.
        console.warn('[setDriverActivePassenger] no pude leer el permiso previo:', e?.message);
      }
      if (yaHayPermisoVivo) return;
    } else {
      // V15/V31 — RETIRAR NO PUEDE BORRAR UN PERMISO QUE PUSO EL SERVIDOR Y
      // SIGUE EN USO. Al (re)montarse el mapa sin viaje activo se llama con
      // null; para el repartidor eso borraba el permiso de su pedido de comida
      // (el disparador no lo repone hasta otro cambio del pedido) y el cliente
      // se quedaba sin mapa en plena entrega. Ese permiso lo retira el propio
      // servidor al cerrarse el pedido o el viaje, y además caduca solo.
      try {
        const previo: any =
          Platform.OS !== 'web'
            ? (await nativeFirestore().collection('driverLocations').doc(driverId).get())?.data()
            : (await getDoc(doc(db, 'driverLocations', driverId))).data();
        const hasta = previo?.activePassengerUntil;
        const ms = typeof hasta?.toMillis === 'function' ? hasta.toMillis() : 0;
        const rideDelPermiso = String(previo?.activePassengerRideId ?? '').trim();
        const vigenteDelServidor =
          previo?.activePassengerSource === 'SERVER_TRIGGER' &&
          !!previo?.activePassengerId &&
          !!rideDelPermiso &&
          Number.isFinite(ms) && ms > Date.now();
        if (vigenteDelServidor) {
          // Pedido de comida: solo lo gestiona el servidor.
          if (rideDelPermiso.startsWith('FOOD_')) return;
          // Viaje: se respeta mientras ese viaje siga vivo con este chofer.
          const viaje: any =
            Platform.OS !== 'web'
              ? (await nativeFirestore().collection('rides').doc(rideDelPermiso).get())?.data()
              : (await getDoc(doc(db, 'rides', rideDelPermiso))).data();
          const vivo = ['ACCEPTED', 'ARRIVED', 'IN_PROGRESS'].includes(String(viaje?.status ?? '').toUpperCase());
          if (vivo && String(viaje?.driverId ?? '') === driverId) return;
        }
      } catch (e: any) {
        // Sin poder mirar no se borra: dejar ciego a quien espera su carro o su
        // comida es peor que un permiso que el servidor retira y que caduca solo.
        console.warn('[setDriverActivePassenger] no pude leer el permiso antes de retirarlo:', e?.message);
        return;
      }
    }

    const payload = passengerId
      ? {
          activePassengerId: passengerId,
          activePassengerUntil: selloDentroDe(VENTANA_PERMISO_UBICACION_MS),
          activePassengerSource: 'DRIVER_APP',
        }
      : {
          activePassengerId: null,
          // `null` y no un sello viejo: la regla exige que sea un timestamp
          // VIGENTE, así que un null deniega igual que un vencido, y además no
          // deja rastro de a quién se autorizó.
          activePassengerUntil: null,
          activePassengerSource: 'DRIVER_APP_RETIRADO',
        };
    try {
      if (Platform.OS !== 'web') {
        await nativeFirestore().collection('driverLocations').doc(driverId).set(payload, { merge: true });
        return;
      }
      await setDoc(doc(db, 'driverLocations', driverId), payload, { merge: true });
    } catch (e: any) {
      console.warn('[setDriverActivePassenger]', e?.message);
    }
  },

  /**
   * DATOS DE VERIFICACIÓN DEL RESTAURANTE (cédula del dueño, foto del local).
   * Van a una subcolección PRIVADA porque /restaurants es de lectura pública
   * (el catálogo lo ve todo el mundo, incluso sin login). La auditoría detectó
   * que la cédula del dueño quedaba scrapeable con un simple curl.
   * Solo el dueño y el admin pueden leer esta subcolección.
   */
  async setRestaurantVerification(restaurantId: string, data: any) {
    if (Platform.OS !== 'web') {
      await nativeFirestore()
        .collection('restaurants').doc(restaurantId)
        .collection('private').doc('verification')
        .set({ ...data, updatedAt: nativeFirestore.FieldValue.serverTimestamp() }, { merge: true });
      return;
    }
    await setDoc(
      doc(db, `restaurants/${restaurantId}/private/verification`),
      { ...data, updatedAt: serverTimestamp() },
      { merge: true }
    );
  },

  async updateRestaurantMenu(restaurantId: string, menu: any[]) {
    // FIX BLOQUEADOR (auditoría) — SDK nativo en móvil (ver createRestaurant).
    if (Platform.OS !== 'web') {
      await nativeFirestore().collection('restaurants').doc(restaurantId).set({ menu }, { merge: true });
      return;
    }
    const docRef = doc(db, 'restaurants', restaurantId);
    await setDoc(docRef, { menu }, { merge: true });
  },

  /**
   * Registra el pago de PUBLICIDAD del restaurante para que un admin lo
   * verifique contra la cuenta bancaria y encienda el banner.
   *
   * El restaurante NO paga suscripción: recibir pedidos es gratis. Esto es solo
   * el anuncio, que él activa cuando quiere. El doc nace en PENDING_REVIEW y las
   * reglas impiden que nadie desde el cliente lo apruebe: encender `isPremium`
   * es exclusivo de la Cloud Function adminApproveAdPayment.
   */
  /**
   * Deja constancia de un SOS con la ubicación de quien lo activó.
   *
   * Antes el botón SOS solo abría el marcador del 911 y la app le decía al
   * usuario que había "transmitido su GPS y su perfil biométrico". Era mentira:
   * no salía nada hacia ningún lado y no quedaba ni un registro. Si algo pasaba
   * de verdad, Go Safely no tenía forma de saberlo ni de ayudar después.
   *
   * Ahora la alerta queda guardada y dispara un aviso al equipo. La llamada al
   * 911 la sigue haciendo la persona — eso ninguna app puede hacerlo por ella.
   */
  async registrarSOS(datos: {
    uid: string | null;
    nombre: string | null;
    telefono: string | null;
    lat: number | null;
    lng: number | null;
    rideId: string | null;
    rol: string | null;
    /**
     * Si `lat`/`lng` vienen de una lectura REAL del GPS o de una posición
     * aproximada. Quien atienda la alerta necesita saber cuánto puede fiarse
     * del punto: en una emergencia, buscar en la cuadra equivocada cuesta
     * tiempo que no hay.
     */
    ubicacionReal?: boolean;
    /**
     * Qué disparó la alerta. 'SOS' es el botón de pánico; 'ZONA_PELIGROSA' es
     * el reporte del repartidor cuando la dirección de entrega resulta
     * insegura. Van a la misma bandeja porque las atiende el mismo equipo,
     * pero se separan aquí para poder priorizar el pánico real.
     * Por omisión 'SOS', que es como se llamaba a este método antes.
     */
    tipo?: 'SOS' | 'ZONA_PELIGROSA' | 'ESPERA_EXCEDIDA';
  }) {
    const payload = { tipo: 'SOS' as const, ...datos, atendido: false };
    if (Platform.OS !== 'web') {
      await nativeFirestore()
        .collection('sos_alerts')
        .add({ ...payload, createdAt: nativeFirestore.FieldValue.serverTimestamp() });
      return;
    }
    await addDoc(collection(db, 'sos_alerts'), { ...payload, createdAt: serverTimestamp() });
  },

  async crearPagoPublicidad(datos: {
    ownerId: string;
    restaurantId: string;
    restaurantName?: string;
    amount: number;
    /** BASICO (RD$1,000) o DESTACADO (RD$2,000). No son membresías: el
     *  restaurante recibe pedidos gratis pague o no. */
    plan?: 'BASICO' | 'DESTACADO';
    planNombre?: string;
    method: 'TRANSFER' | 'TOKE' | 'CARD';
    bankId?: string;
    receiptUrl?: string | null;
  }) {
    const payload = {
      ...datos,
      restaurantName: datos.restaurantName ?? '',
      plan: datos.plan ?? 'DESTACADO',
      planNombre: datos.planNombre ?? 'Anuncio Destacado',
      bankId: datos.bankId ?? null,
      receiptUrl: datos.receiptUrl ?? null,
      status: 'PENDING_REVIEW',
    };
    // FIX BLOQUEADOR (auditoría) — SDK nativo en móvil: el SDK web va anónimo
    // en el APK y las reglas rechazarían la escritura.
    if (Platform.OS !== 'web') {
      await nativeFirestore()
        .collection('ad_payments')
        .add({ ...payload, createdAt: nativeFirestore.FieldValue.serverTimestamp() });
      return;
    }
    await addDoc(collection(db, 'ad_payments'), { ...payload, createdAt: serverTimestamp() });
  },

  async updateRestaurantPromos(restaurantId: string, activePromotions: any[]) {
    // FIX BLOQUEADOR (auditoría) — SDK nativo en móvil (ver createRestaurant).
    if (Platform.OS !== 'web') {
      await nativeFirestore().collection('restaurants').doc(restaurantId).set({ activePromotions }, { merge: true });
      return;
    }
    const docRef = doc(db, 'restaurants', restaurantId);
    await setDoc(docRef, { activePromotions }, { merge: true });
  },

  listenToRestaurantOrders(restaurantId: string, callback: (orders: any[]) => void) {
    // FIX RESTAURANT ↔ DELIVERY — Antes este listener iba sin auth en mobile
    // → restaurante NUNCA veía sus órdenes entrantes → repartidor NUNCA
    // recibía pedidos READY → toda la cadena de food delivery rota.
    //
    // FIX P0 (causa raíz #4) — El campo era el equivocado. Esto consultaba
    // `where restaurantId == uid`, pero la regla de /food_orders solo autoriza
    // por `restaurantOwnerId` / `customerId` / `driverId` / status READY. En
    // Firestore una consulta ES un `list` y se evalúa contra el conjunto
    // POTENCIAL, no documento a documento: la consulta entera salía
    // permission-denied, el `catch` la tragaba con un console.warn y el dueño
    // veía su cocina vacía para siempre. `orders.ts` escribe
    // `restaurantOwnerId` en todos los pedidos y el doc de /restaurants tiene
    // id = uid del dueño, así que basta cambiar el campo: ni migración de
    // datos ni cambio de reglas.
    //
    // ⚠️ FALTA UN ÍNDICE que no puedo crear desde aquí:
    //    food_orders(restaurantOwnerId ASC, createdAt DESC)
    // en firestore.indexes.json (archivo de otro frente). Mientras no exista,
    // la consulta ordenada falla con FAILED_PRECONDITION — por eso abajo hay
    // un respaldo SIN orderBy que ordena en memoria: el panel funciona igual
    // desde el primer día y mejora solo en cuanto se despliegue el índice.
    //
    // V04 — El respaldo pide los pedidos VIVOS, no 50 cualesquiera: sin orderBy
    // Firestore devuelve por id de documento (aleatorio) y, pasados 50 pedidos
    // históricos, un PENDING nuevo podía quedarse fuera del panel. Solo lleva
    // igualdades (== e in), así que no necesita índice compuesto.
    const ESTADOS_VIVOS = ['PENDING', 'PREPARING', 'READY', 'ASSIGNED', 'PICKED_UP'];
    const sortByCreated = (orders: any[]) => {
      orders.sort((a: any, b: any) => {
        const tA = a.createdAt?.toMillis?.() || 0;
        const tB = b.createdAt?.toMillis?.() || 0;
        return tB - tA;
      });
      return orders;
    };
    let cancelar: (() => void) | null = null;
    let cancelada = false;
    let reintentado = false;
    const cerrar = () => {
      cancelada = true;
      try { cancelar?.(); } catch { /* ya estaba cerrada */ }
      cancelar = null;
    };
    /** El respaldo que nace tarde no puede quedar huérfano si ya cerraron. */
    const adoptar = (respaldo: () => void) => {
      if (cancelada) { try { respaldo(); } catch { /* nada */ } }
      else cancelar = respaldo;
    };

    if (Platform.OS !== 'web') {
      const respaldoSinOrden = (): (() => void) =>
        nativeFirestore()
          .collection('food_orders')
          .where('restaurantOwnerId', '==', restaurantId)
          .where('status', 'in', ESTADOS_VIVOS)
          .onSnapshot((snap: any) => {
            const orders = snap.docs.map((d: any) => ({ id: d.id, ...d.data() }));
            callback(sortByCreated(orders));
          }, (e2: any) => {
            console.warn('[listenToRestaurantOrders] respaldo también falló:', e2?.message);
            callback([]);
          }) as () => void;

      const inicial = nativeFirestore()
        .collection('food_orders')
        .where('restaurantOwnerId', '==', restaurantId)
        // TOPE — se bajaban todos los pedidos históricos; los DELIVERED y
        // CANCELLED ni se muestran en el panel. Desperdicio puro.
        .orderBy('createdAt', 'desc')
        .limit(50)
        .onSnapshot((snap: any) => {
          const orders = snap.docs.map((d: any) => ({ id: d.id, ...d.data() }));
          callback(sortByCreated(orders));
        }, (err: any) => {
          console.warn('[listenToRestaurantOrders]', err?.message);
          if (reintentado) { callback([]); return; }
          reintentado = true;
          adoptar(respaldoSinOrden());
        }) as () => void;
      if (!reintentado && !cancelada) cancelar = inicial;
      else { try { inicial(); } catch { /* nada */ } }
      return cerrar;
    }

    const ordersRef = collection(db, 'food_orders');
    const respaldoWeb = (): (() => void) =>
      onSnapshot(
        query(ordersRef, where('restaurantOwnerId', '==', restaurantId), where('status', 'in', ESTADOS_VIVOS)),
        (snapshot) => {
          const orders = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
          callback(sortByCreated(orders));
        },
        (e2) => {
          console.warn('[listenToRestaurantOrders] respaldo web también falló:', e2?.message);
          callback([]);
        }
      );
    const q = query(
      ordersRef,
      where('restaurantOwnerId', '==', restaurantId),
      orderBy('createdAt', 'desc'),
      limit(50)
    );
    const inicialWeb = onSnapshot(
      q,
      (snapshot) => {
        const orders = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        callback(sortByCreated(orders));
      },
      (err) => {
        console.warn('[listenToRestaurantOrders] web error:', err?.message);
        if (reintentado) { callback([]); return; }
        reintentado = true;
        adoptar(respaldoWeb());
      }
    );
    if (!reintentado && !cancelada) cancelar = inicialWeb;
    else { try { inicialWeb(); } catch { /* nada */ } }
    return cerrar;
  },

  async updateFoodOrderStatus(orderId: string, newStatus: string) {
    // FIX BLOQUEADOR (auditoría) — En móvil el SDK web va anónimo → el
    // restaurante no podía pasar el pedido PENDING→PREPARING→READY, así que el
    // trigger dispatchFoodOrder y el feed del repartidor nunca disparaban.
    // nativeFirestore() hereda la sesión nativa.
    if (Platform.OS !== 'web') {
      await nativeFirestore().collection('food_orders').doc(orderId).update({
        status: newStatus,
        updatedAt: nativeFirestore.FieldValue.serverTimestamp(),
      });
      return;
    }
    const orderRef = doc(db, 'food_orders', orderId);
    return await updateDoc(orderRef, {
      status: newStatus,
      updatedAt: serverTimestamp()
    });
  },

  // --- MOTORISTAS (FOOD DELIVERY) ---
  listenToAvailableDeliveries(callback: (orders: any[]) => void) {
    // FIX DISCOVERY — SDK nativo en mobile.
    if (Platform.OS !== 'web') {
      return nativeFirestore()
        .collection('food_orders')
        .where('status', '==', 'READY')
        .onSnapshot((snap: any) => {
          const orders = snap.docs.map((d: any) => ({ id: d.id, ...d.data() }));
          callback(orders);
        }, (err: any) => { console.warn('[listenToAvailableDeliveries] error:', err?.message); callback([]); });
    }
    const ordersRef = collection(db, 'food_orders');
    // Solo órdenes listas para recoger
    const q = query(ordersRef, where('status', '==', 'READY'));

    return onSnapshot(
      q,
      (snapshot) => {
        const orders = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        callback(orders);
      },
      (err) => {
        console.warn('[listenToAvailableDeliveries] web error:', err?.message);
        callback([]);
      }
    );
  },

  /**
   * EL PEDIDO QUE ESTE REPARTIDOR YA TOMÓ Y TODAVÍA NO HA ENTREGADO.
   *
   * Por qué hace falta: la ÚNICA entrada a la pantalla de entrega
   * (`/delivery-active`) era el salto que ocurre justo después de reclamar el
   * pedido. Si el repartidor cerraba la app, se le acababa la batería o
   * simplemente volvía atrás, **no tenía forma de regresar a su propia
   * entrega**: el pedido ya no está READY, así que desaparecía también del
   * feed. Se quedaba con la comida encima y la app sin camino.
   *
   * `in` con dos estados va sobre el índice compuesto (driverId, status) que ya
   * está declarado y desplegado — comprobado antes de escribir esto, para no
   * dejar una consulta que falle en silencio en el teléfono de alguien.
   *
   * Las reglas lo permiten sin tocarlas: `/food_orders` deja leer al
   * `driverId` asignado.
   */
  listenToMiEntregaActiva(driverId: string, callback: (pedido: any | null) => void) {
    const ESTADOS = ['ASSIGNED', 'PICKED_UP'];
    const primero = (docs: any[]) => callback(docs.length ? docs[0] : null);

    if (Platform.OS !== 'web') {
      return nativeFirestore()
        .collection('food_orders')
        .where('driverId', '==', driverId)
        .where('status', 'in', ESTADOS)
        .limit(1)
        .onSnapshot(
          (snap: any) => primero(snap.docs.map((d: any) => ({ id: d.id, ...d.data() }))),
          (err: any) => {
            // Sin dato NO se inventa una entrega: se avisa y se sigue.
            console.warn('[listenToMiEntregaActiva]', err?.message);
            callback(null);
          }
        );
    }

    const q = query(
      collection(db, 'food_orders'),
      where('driverId', '==', driverId),
      where('status', 'in', ESTADOS),
      limit(1)
    );
    return onSnapshot(
      q,
      (snap) => primero(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => {
        console.warn('[listenToMiEntregaActiva] web:', err?.message);
        callback(null);
      }
    );
  },

  /**
   * ¿Es la PRIMERA vez que este cliente pide en este negocio?
   *
   * Lo usa el carrito para enseñar el descuento del 50% antes de pagar. El que
   * MANDA es el servidor (`createFoodOrder` hace esta misma consulta con el
   * Admin SDK); esto es solo para que la pantalla no mienta.
   *
   * Devuelve `null` cuando no se pudo averiguar — y eso NO es «sí». Quien
   * llama tiene que tratarlo como «no sé» y no pintar descuento: si lo pintara
   * de más, el cliente vería un número que el servidor no le va a cobrar. Al
   * contrario no hay daño: el servidor se lo descuenta igual y paga menos de
   * lo que vio.
   *
   * Las reglas lo permiten sin tocarlas: `/food_orders` deja leer al
   * `customerId`, y la consulta filtra justo por él. Necesita el índice
   * food_orders(customerId, restaurantId), declarado en firestore.indexes.json.
   */
  async yaPidioEnEsteNegocio(uid: string, restaurantId: string): Promise<boolean | null> {
    if (!uid || !restaurantId) return null;
    try {
      if (Platform.OS !== 'web') {
        const snap: any = await nativeFirestore()
          .collection('food_orders')
          .where('customerId', '==', uid)
          .where('restaurantId', '==', restaurantId)
          .limit(1)
          .get();
        return !snap.empty;
      }
      const snap = await getDocs(
        query(
          collection(db, 'food_orders'),
          where('customerId', '==', uid),
          where('restaurantId', '==', restaurantId),
          limit(1)
        )
      );
      return !snap.empty;
    } catch (e: any) {
      console.warn('[yaPidioEnEsteNegocio]', e?.message ?? e);
      return null;
    }
  },

  // ══ `assignDeliveryToDriver` SE FUE, Y NO SE VUELVE A AÑADIR ════════════
  //
  // Escribía `status:'ASSIGNED'` + `driverId` DIRECTO sobre /food_orders desde
  // el teléfono del repartidor. Eso nunca pudo funcionar en producción: las
  // reglas (firestore.rules, match /food_orders) lo deniegan por tres motivos a
  // la vez —quien escribe todavía no es el `driverId` del pedido, 'ASSIGNED'
  // está entre los estados prohibidos, y `driverId` está en la lista negra de
  // campos—. La transacción que tenía dentro era honesta pero inútil: la denegación
  // llegaba antes. Su única llamada (el botón "Aceptar" del panel del mapa) no
  // tenía try/catch, así que el rechazo se perdía como promesa no capturada y el
  // repartidor no veía ni el pedido ni un error.
  //
  // La asignación la hace la callable `claimFoodOrder`
  // (functions/src/foodDispatch.ts) con Admin SDK: es atómica de verdad —el
  // segundo repartidor recibe "ya fue tomado"— y además comprueba rol, KYC,
  // verificación de chofer y cuota al día, que un write desde el cliente no
  // puede comprobar. Se llama con `callClaimFoodOrder` (services/cloudFunctions).
  // Si alguna pantalla necesita tomar un pedido, usa esa; volver a poner un
  // write directo aquí solo reproduce el botón mudo.

  /**
   * SEGURIDAD — Lee el PIN de entrega desde la subcolección PRIVADA
   * /food_orders/{id}/private/security. Las reglas solo permiten al CLIENTE
   * dueño leerlo (el repartidor NO). El repartidor ya no ve el PIN en ninguna
   * parte; la verificación de entrega es 100% server-side.
   */
  listenToOrderPin(orderId: string, callback: (pin: string | null) => void) {
    if (Platform.OS !== 'web') {
      return nativeFirestore()
        .collection('food_orders').doc(orderId)
        .collection('private').doc('security')
        .onSnapshot((snap: any) => {
          callback(snap.exists() ? String(snap.data()?.pin ?? '') : null);
        }, (err: any) => { console.warn('[listenToOrderPin]', err?.message); callback(null); });
    }
    const ref = doc(db, 'food_orders', orderId, 'private', 'security');
    return onSnapshot(ref, (snap) => {
      callback(snap.exists() ? String((snap.data() as any)?.pin ?? '') : null);
    }, () => callback(null));
  },

  async getFoodOrderById(orderId: string) {
    const orderRef = doc(db, 'food_orders', orderId);
    const snap = await getDoc(orderRef);
    if (snap.exists()) {
      return { id: snap.id, ...snap.data() };
    }
    return null;
  },

  listenToFoodOrderById(orderId: string, callback: (order: any | null) => void) {
    if (Platform.OS !== 'web') {
      return nativeFirestore().collection('food_orders').doc(orderId).onSnapshot((snap: any) => {
        if (snap.exists()) callback({ id: snap.id, ...snap.data() });
        else callback(null);
      }, (err: any) => { console.warn('[listenToFoodOrderById]', err?.message); callback(null); });
    }
    const orderRef = doc(db, 'food_orders', orderId);
    return onSnapshot(
      orderRef,
      (snap) => {
        if (snap.exists()) {
          callback({ id: snap.id, ...snap.data() });
        } else {
          callback(null);
        }
      },
      (err) => {
        console.warn('[listenToFoodOrderById] web error:', err?.message);
        callback(null);
      }
    );
  },

  /**
   * DATOS PRIVADOS DEL PEDIDO — H15 (auditoría 16-sep-2026).
   *
   * El teléfono del cliente (`private/contacto`) y el comprobante de la
   * transferencia (`private/comprobante`) salieron del documento principal: un
   * pedido READY lo lee cualquier repartidor verificado. Las reglas dejan leer
   * `contacto` al cliente, al restaurante y al repartidor YA asignado, y
   * `comprobante` al cliente y al restaurante.
   *
   * `onError` recibe el fallo aparte del "no existe" (`onData(null)`): no es lo
   * mismo "este pedido no trae comprobante" que "no se pudo leer".
   */
  escucharPrivadoDelPedido(
    orderId: string,
    docId: 'contacto' | 'comprobante',
    onData: (data: any | null) => void,
    onError?: (e: any) => void
  ): () => void {
    const fallar = (e: any) => {
      console.warn(`[escucharPrivadoDelPedido] ${docId}:`, e?.code, e?.message);
      onError?.(e);
    };
    if (Platform.OS !== 'web') {
      return nativeFirestore()
        .collection('food_orders').doc(orderId)
        .collection('private').doc(docId)
        .onSnapshot(
          (snap: any) => onData(snap?.exists?.() ? snap.data() : null),
          fallar
        ) as () => void;
    }
    return onSnapshot(
      doc(db, 'food_orders', orderId, 'private', docId),
      (snap) => onData(snap.exists() ? snap.data() : null),
      fallar
    ) as () => void;
  },

  /**
   * Guarda el enlace del comprobante que el CLIENTE sube desde su recibo (H15).
   * La regla solo admite estos dos campos y exige la hora del servidor.
   */
  async guardarComprobanteDelPedido(orderId: string, receiptImage: string) {
    if (Platform.OS !== 'web') {
      await nativeFirestore()
        .collection('food_orders').doc(orderId)
        .collection('private').doc('comprobante')
        .set({
          receiptImage,
          receiptUploadedAt: nativeFirestore.FieldValue.serverTimestamp(),
        });
      return;
    }
    await setDoc(doc(db, 'food_orders', orderId, 'private', 'comprobante'), {
      receiptImage,
      receiptUploadedAt: serverTimestamp(),
    });
  },

  // --- CHAT customer ↔ repartidor en una food order ---
  // Subcolección /food_orders/{orderId}/messages
  listenToFoodOrderMessages(orderId: string, callback: (msgs: any[]) => void) {
    if (Platform.OS !== 'web') {
      return nativeFirestore()
        .collection('food_orders')
        .doc(orderId)
        .collection('messages')
        .orderBy('createdAt', 'asc')
        .onSnapshot((snap: any) => {
          callback(snap.docs.map((d: any) => ({ id: d.id, ...d.data() })));
        }, (err: any) => { console.warn('[listenToFoodOrderMessages]', err?.message); callback([]); });
    }
    const ref = collection(db, 'food_orders', orderId, 'messages');
    const q = query(ref, orderBy('createdAt', 'asc'));
    return onSnapshot(
      q,
      (snap) => {
        const msgs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        callback(msgs);
      },
      (err) => {
        console.warn('[listenToFoodOrderMessages] web error:', err?.message);
        callback([]);
      }
    );
  },

  async sendFoodOrderMessage(orderId: string, senderId: string, recipientId: string, text: string) {
    // Con el SDK web en móvil este envío se denegaba: el mensaje desaparecía
    // del chat del pedido sin decir nada.
    return await agregarDoc(['food_orders', orderId, 'messages'], {
      senderId,
      recipientId,
      text: text.trim().slice(0, 500),
      createdAt: selloServidor(),
    });
  },

  // --- DIRECCIONES GUARDADAS DEL USUARIO ---
  // Subcolección /users/{uid}/addresses — controlada por reglas isSelf.
  // Cada dirección: { label, line1, lat?, lng?, isDefault?, createdAt }
  listenToUserAddresses(uid: string, callback: (addresses: any[]) => void) {
    const sortItems = (items: any[]) => {
      items.sort((a: any, b: any) => {
        if (a.isDefault && !b.isDefault) return -1;
        if (!a.isDefault && b.isDefault) return 1;
        const tA = a.createdAt?.toMillis?.() ?? 0;
        const tB = b.createdAt?.toMillis?.() ?? 0;
        return tB - tA;
      });
      return items;
    };
    if (Platform.OS !== 'web') {
      return nativeFirestore()
        .collection('users')
        .doc(uid)
        .collection('addresses')
        .onSnapshot((snap: any) => {
          const items = snap.docs.map((d: any) => ({ id: d.id, ...d.data() }));
          callback(sortItems(items));
        }, (err: any) => { console.warn('[listenToUserAddresses]', err?.message); callback([]); });
    }
    const ref = collection(db, 'users', uid, 'addresses');
    return onSnapshot(
      ref,
      (snap) => {
        const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        callback(sortItems(items));
      },
      (err) => {
        console.warn('[listenToUserAddresses] web error:', err?.message);
        callback([]);
      }
    );
  },

  async addUserAddress(
    uid: string,
    payload: { label: string; line1: string; lat?: number; lng?: number; isDefault?: boolean; reference?: string }
  ) {
    // Las tres funciones de direcciones escribían con el SDK web, que en móvil
    // va sin sesión: "Guardar dirección" no guardaba nada. Y sin dirección no
    // se puede completar un pedido de comida, así que rompía también el
    // checkout.
    const ids = await listarIdsDeDirecciones(uid);
    // Si esta dirección se marca default, quitar default de las otras
    if (payload.isDefault) {
      for (const item of ids) {
        if (item.isDefault) {
          await actualizarDoc(['users', uid, 'addresses', item.id], { isDefault: false });
        }
      }
    }
    return await agregarDoc(['users', uid, 'addresses'], {
      ...payload,
      createdAt: selloServidor(),
    });
  },

  async setAddressAsDefault(uid: string, addressId: string) {
    const ids = await listarIdsDeDirecciones(uid);
    for (const item of ids) {
      await actualizarDoc(['users', uid, 'addresses', item.id], {
        isDefault: item.id === addressId,
      });
    }
  },

  async deleteUserAddress(uid: string, addressId: string) {
    await borrarDoc(['users', uid, 'addresses', addressId]);
  },

  /**
   * V49 — Con el SDK que toca (en Android el web va sin sesión y la regla
   * denegaba SIEMPRE) y devolviendo el id, para poder cerrar la ruta después.
   * `schoolId` sale del perfil del chofer (el uid del director que lo
   * certificó); si el perfil no lo trae se omite: poner el uid del propio
   * chofer no le abría la ruta a ningún director.
   */
  async createSchoolRoute(driverId: string, routeData: any): Promise<string> {
    const { schoolId: _ignorado, ...resto } = routeData ?? {};
    let schoolId: string | null = null;
    try {
      const perfil = await this.getUserProfile(driverId);
      const candidato = String(perfil?.schoolId ?? '').trim();
      if (candidato && candidato !== driverId) schoolId = candidato;
    } catch (e: any) {
      console.warn('[createSchoolRoute] no pude leer el perfil del chofer:', e?.message);
    }
    return await agregarDoc(['school_routes'], {
      ...resto,
      driverId,
      ...(schoolId ? { schoolId } : {}),
      createdAt: selloServidor(),
    });
  },

  /** V49 — Cierra la ruta: sin esto quedaba `active` para siempre. */
  async finishSchoolRoute(routeId: string) {
    if (!routeId) return;
    await actualizarDoc(['school_routes', routeId], {
      status: 'finished',
      endedAt: selloServidor(),
    });
  },

  async registerStudent(studentData: any) {
    return await addDoc(collection(db, 'students'), {
      ...studentData,
      createdAt: serverTimestamp()
    });
  },

  /**
   * Escucha en tiempo real los estudiantes asignados a la ruta de un driver.
   * Las reglas Firestore permiten leer cuando el solicitante es el driver propio o
   * el director de la escuela del estudiante.
   */
  listenToRouteStudents(driverId: string, callback: (students: any[]) => void) {
    if (Platform.OS !== 'web') {
      return nativeFirestore()
        .collection('students')
        .where('driverId', '==', driverId)
        .onSnapshot((snap: any) => {
          callback(snap.docs.map((d: any) => ({ id: d.id, ...d.data() })));
        }, (err: any) => { console.warn('[listenToRouteStudents]', err?.message); callback([]); });
    }
    const q = query(collection(db, 'students'), where('driverId', '==', driverId));
    return onSnapshot(
      q,
      (snapshot) => {
        callback(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
      },
      (err) => {
        console.warn('[listenToRouteStudents] web error:', err?.message);
        callback([]);
      }
    );
  },

  /**
   * Marca el status de un estudiante en la ruta: 'Esperando abordaje' → 'A bordo' → 'Entregado'.
   */
  async updateStudentRouteStatus(studentId: string, status: string) {
    // AUDIT FIX SCHOOL — Antes solo actualizábamos /students/{id} root, el
    // padre nunca veía el cambio porque su UI escucha /users/{parentId}/
    // students. Ahora leemos el parentId del doc root, mirroreamos el
    // update en la sub del padre. Ambas writes en paralelo — el listener
    // del padre y el del driver detectan el cambio casi simultáneo.
    const payload = {
      status,
      lastStatusAt: serverTimestamp(),
    };
    if (Platform.OS !== 'web') {
      const rootSnap = await nativeFirestore().collection('students').doc(studentId).get();
      // En @react-native-firebase v24 `exists` es un MÉTODO, no un booleano:
      // sin los paréntesis la condición era siempre verdadera y la rama del
      // "documento no existe" estaba muerta.
      const parentId = rootSnap.exists() ? String(rootSnap.data()?.parentId ?? '') : '';
      const writes: Promise<any>[] = [
        nativeFirestore().collection('students').doc(studentId).update(payload),
      ];
      // Buscar la sub-doc del padre por rootStudentId (guardado como
      // rootId al momento de addStudent). Si no la encontramos, no
      // fatalizamos — el root queda al día.
      if (parentId) {
        try {
          const subSnap = await nativeFirestore()
            .collection('users')
            .doc(parentId)
            .collection('students')
            .where('rootId', '==', studentId)
            .limit(1)
            .get();
          if (!subSnap.empty) {
            writes.push(subSnap.docs[0].ref.update(payload));
          }
        } catch (e: any) {
          console.warn('[updateStudentRouteStatus] mirror padre falló:', e?.message);
        }
      }
      await Promise.all(writes);
      return;
    }
    const ref = doc(db, 'students', studentId);
    await updateDoc(ref, payload);
  }
};
